/**
 * Mock seed data for the static prototype.
 *
 * This file is the single source of truth for all data the prototype shows.
 * It is shaped to match the frontend TypeScript types directly (camelCase),
 * so the service layer can return these objects with little to no mapping.
 *
 * Derived from the backend contract-test fixtures, but adapted to the shapes
 * the React app actually consumes. There is NO backend: everything here lives
 * in memory and is optionally persisted to localStorage (see store.ts).
 */

import { AdminApplicationStatus, ApplicationAlert } from '../types/admin';
import { User } from '../types/auth';
import { SectionStatus, SectionType } from '../types';
import {
  buildCohortApplicationRecords,
  buildCohortAssignmentRecords,
  buildCohortUsers,
} from './bulkCohort';

// ---------------------------------------------------------------------------
// Date helpers — offsets relative to "now" so deadlines feel live.
// ---------------------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;

export const daysFromNow = (days: number): string =>
  new Date(Date.now() + days * DAY_MS).toISOString();

const now = (): string => new Date().toISOString();

// ---------------------------------------------------------------------------
// Demo credentials — fixed accounts, one per role. All share one password.
// ---------------------------------------------------------------------------

export const DEMO_PASSWORD = '123';

export interface DemoAccount {
  email: string;
  password: string;
  role: 'researcher' | 'evaluator' | 'admin';
  label: string;
}

export const DEMO_ACCOUNTS: DemoAccount[] = [
  {
    email: 'investigador@test.com',
    password: DEMO_PASSWORD,
    role: 'researcher',
    label: 'Investigador',
  },
  {
    email: 'evaluador@test.com',
    password: DEMO_PASSWORD,
    role: 'evaluator',
    label: 'Evaluador',
  },
  {
    email: 'admin@test.com',
    password: DEMO_PASSWORD,
    role: 'admin',
    label: 'Administrador',
  },
];

// ---------------------------------------------------------------------------
// Internal record types (richer than the API DTOs; services project from them)
// ---------------------------------------------------------------------------

export interface StoredUser extends User {
  isExternal: boolean;
  isConfirmed: boolean;
  passwordSetupToken?: string;
  passwordSetupExpiresAt?: string;
}

export interface StoredApplication {
  id: string;
  userId: string;
  formTemplateId: string;
  applicationNumber: string;
  status: AdminApplicationStatus;
  sectionAnswers: Partial<Record<SectionType, any>>;
  sectionStatus: Partial<Record<SectionType, SectionStatus>>;
  lastSaved: string;
  complianceConfirmed: boolean;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
  awardNotifiedAt: string | null;
  reviewerAssignment?: { adminId: string; adminName: string; assignedAt: string };
  statusHistory: Array<{
    status: AdminApplicationStatus;
    changedBy: string;
    changedByName: string;
    changedAt: string;
    notes?: string;
  }>;
}

export interface StoredEvaluation {
  id: string;
  assignmentId: string;
  applicationId: string;
  evaluatorId: string;
  criteriaEvaluations: Record<string, { score: number; comments: string | null; maxScore: number }>;
  budgetComments: string | null;
  overallComments: string | null;
  conflictsDeclared: boolean;
  conflictDetails: string | null;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface StoredAssignment {
  id: string;
  applicationId: string;
  evaluatorId: string;
  assignedBy: string;
  assignedAt: string;
  // acceptedAt: when the evaluator accepted the invitation (null if not accepted
  // yet). Optional in seed data; the mapper falls back to assignedAt for
  // non-pending assignments so existing scenarios keep working.
  acceptedAt?: string | null;
  // pending            = invitation sent, evaluator hasn't responded yet
  // accepted_not_started = accepted the invitation but hasn't opened the form
  // in_progress        = has opened/started filling the evaluation form
  // completed          = evaluation submitted
  // declined           = rejected invitation (not conflict — different path)
  // conflict_declared  = declared conflict of interest (EVA-021)
  status: 'pending' | 'accepted_not_started' | 'in_progress' | 'completed' | 'declined' | 'conflict_declared';
  evaluationSubmittedAt: string | null;
  conflictDeclared: boolean;
  conflictStatus: 'no_conflict' | 'conflict_declared' | null;
  conflictDetails: string | null;
  conflictDeclaredAt: string | null;
  declineReason?: 'thematic' | 'conflict' | 'other' | null;
  declineNote?: string | null;
  // Batch that created this assignment, when it came from the bulk-assignment
  // wizard. Optional: seeded and manually created assignments do not carry it.
  importBatchId?: string;
  // When the invitation email actually went out. Null means the assignment
  // exists but nobody has been told — `status: 'pending'` alone cannot express
  // that, which is why coverage used to read "Con invitación" for assignments
  // that were never notified. Mirrors StoredApplication.awardNotifiedAt.
  invitationSentAt?: string | null;
}

export interface EvaluatorAdminNote {
  id: string;
  text: string;
  createdAt: string;
  createdByName: string;
}

// Admin-only profile for each evaluator — never exposed to the evaluator themselves.
// Visible in the Evaluaciones dashboard and in the evaluator assignment modal (EVA-012).
export interface EvaluatorAdminProfile {
  evaluatorId: string;
  score: number | null;
  notes: EvaluatorAdminNote[];
}

export interface StoredReminder {
  id: string;
  evaluatorId: string;
  assignmentIds: string[];
  templateType: 'invitation' | 'evaluation';
  message: string;
  sentAt: string;
  sentByName: string;
}

export interface StoredFile {
  id: string;
  applicationId: string;
  sectionType: SectionType;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
  downloadUrl: string;
}

export interface SeedData {
  users: StoredUser[];
  formTemplates: any[];
  applications: StoredApplication[];
  assignments: StoredAssignment[];
  evaluations: StoredEvaluation[];
  alerts: ApplicationAlert[];
  files: StoredFile[];
  evaluatorProfiles: EvaluatorAdminProfile[];
  reminders: StoredReminder[];
  contestExclusions: Array<{ templateId: string; evaluatorId: string }>;
}

// ---------------------------------------------------------------------------
// Evaluation criteria — the six keys the frontend renders everywhere.
// ---------------------------------------------------------------------------

export const CRITERIA_KEYS = [
  'originality_risk',
  'national_relevance',
  'global_scalability',
  'international_collaboration',
  'technical_viability',
  'results_quality',
] as const;

const PDF = 'application/pdf';
const XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

const buildUsers = (): StoredUser[] => [
  {
    id: '11111111-1111-4111-8111-000000000001',
    email: 'investigador@test.com',
    name: 'María González',
    role: 'researcher',
    isActive: true,
    isExternal: false,
    isConfirmed: true,
    createdAt: daysFromNow(-120),
    lastLoginAt: daysFromNow(-1),
    userProfile: {
      run: '11.111.111-1',
      affiliation: 'Pontificia Universidad Católica de Chile',
      facultadUC: 'Ingeniería',
      unidadAcademica: 'Departamento de Ingeniería Eléctrica',
      doctorateYear: 2020,
      doctorateMonth: 6,
      researcherType: 'emerging',
      canApply: true,
    },
  },
  {
    id: '11111111-1111-4111-8111-000000000006',
    email: 'researcher.consolidated@test.local',
    name: 'Jorge Fuentes',
    role: 'researcher',
    isActive: true,
    isExternal: false,
    isConfirmed: true,
    createdAt: daysFromNow(-200),
    userProfile: {
      run: '11.111.111-6',
      affiliation: 'Pontificia Universidad Católica de Chile',
      facultadUC: 'Ingeniería',
      unidadAcademica: 'Departamento de Ciencia de la Computación',
      doctorateYear: 2010,
      doctorateMonth: 6,
      researcherType: 'consolidated',
      canApply: true,
    },
  },
  {
    id: '11111111-1111-4111-8111-000000000007',
    email: 'researcher.other_unit@test.local',
    name: 'Carolina Rivas',
    role: 'researcher',
    isActive: true,
    isExternal: false,
    isConfirmed: true,
    createdAt: daysFromNow(-90),
    userProfile: {
      run: '11.111.111-7',
      affiliation: 'Pontificia Universidad Católica de Chile',
      facultadUC: 'Medicina',
      unidadAcademica: 'Escuela de Medicina',
      doctorateYear: 2019,
      doctorateMonth: 6,
      researcherType: 'emerging',
      canApply: true,
    },
  },
  // Researcher without posting permission (POS-003 test case)
  {
    id: '11111111-1111-4111-8111-000000000008',
    email: 'investigador.bloqueado@test.local',
    name: 'Luis Paredes',
    role: 'researcher' as const,
    isActive: true,
    isExternal: false,
    isConfirmed: true,
    createdAt: daysFromNow(-30),
    lastLoginAt: daysFromNow(-2),
    userProfile: {
      run: '11.111.111-8',
      affiliation: 'Pontificia Universidad Católica de Chile',
      facultadUC: 'Derecho',
      unidadAcademica: 'Escuela de Derecho',
      doctorateYear: 2022,
      doctorateMonth: 6,
      researcherType: 'emerging' as const,
      canApply: false,
      platformMessage:
        'Tu habilitación está siendo revisada por la DINV. Una vez completada la validación de tus horas académicas podrás crear postulaciones. Contacta a avanza.uc@uc.cl para más información.',
    },
  },
  // Evaluators wired into application_under_review_full
  // TODO(fullstack): userProfile.researcherType is a mock value. The real value comes from the
  // university's researcher registry / identity provider. Evaluators may have a researcher
  // classification that should be fetched alongside their user record.
  {
    id: '22222222-2222-4222-8222-00000000000A',
    email: 'evaluator.a@test.local',
    name: 'Patricia Soto',
    role: 'evaluator',
    isActive: true,
    isExternal: false,
    isConfirmed: true,
    createdAt: daysFromNow(-60),
    lastLoginAt: daysFromNow(-2),
    userProfile: { researcherType: 'consolidated' },
  },
  {
    id: '22222222-2222-4222-8222-00000000000B',
    email: 'evaluador@test.com',
    name: 'Roberto Méndez',
    role: 'evaluator',
    isActive: true,
    isExternal: false,
    isConfirmed: true,
    createdAt: daysFromNow(-60),
    lastLoginAt: daysFromNow(-1),
    userProfile: { researcherType: 'consolidated' },
  },
  {
    id: '22222222-2222-4222-8222-00000000000C',
    email: 'evaluator.c@test.local',
    name: 'Elena Vargas',
    role: 'evaluator',
    isActive: true,
    isExternal: true,
    isConfirmed: true,
    createdAt: daysFromNow(-60),
    lastLoginAt: daysFromNow(-7),
    userProfile: { researcherType: 'emerging' },
  },
  {
    id: '22222222-2222-4222-8222-00000000000D',
    email: 'evaluator.d@test.local',
    name: 'Andrés Tapia',
    role: 'evaluator',
    isActive: true,
    isExternal: false,
    isConfirmed: true,
    createdAt: daysFromNow(-60),
    lastLoginAt: daysFromNow(-10),
    userProfile: { researcherType: 'consolidated' },
  },
  {
    id: '22222222-2222-4222-8222-00000000000E',
    email: 'evaluator.e@test.local',
    name: 'Lucía Herrera',
    role: 'evaluator',
    isActive: true,
    isExternal: true,
    isConfirmed: true,
    createdAt: daysFromNow(-60),
    userProfile: { researcherType: 'emerging' },
  },
  {
    id: '22222222-2222-4222-8222-00000000000F',
    email: 'evaluator.f@test.local',
    name: 'Carmen Riquelme',
    role: 'evaluator',
    isActive: true,
    isExternal: false,
    isConfirmed: true,
    createdAt: daysFromNow(-45),
    lastLoginAt: daysFromNow(-1),
    userProfile: { researcherType: 'consolidated' },
  },
  {
    id: '22222222-2222-4222-8222-000000000010',
    email: 'evaluator.g@test.local',
    name: 'Felipe Bravo',
    role: 'evaluator',
    isActive: true,
    isExternal: false,
    isConfirmed: true,
    createdAt: daysFromNow(-30),
    lastLoginAt: daysFromNow(-3),
    userProfile: { researcherType: 'emerging' },
  },
  {
    id: '22222222-2222-4222-8222-000000000011',
    email: 'maria.externos@consultora.cl',
    name: 'María Eugenia Contreras',
    role: 'evaluator',
    isActive: true,
    isExternal: true,
    isConfirmed: false,
    createdAt: daysFromNow(-10),
    userProfile: { researcherType: 'emerging' },
  },
  // ── Sin asignación (disponible = 10, sin invitaciones) ──────────────────
  { id: '22222222-2222-4222-8222-000000000015', email: 'a.vera@uc.cl',             name: 'Andrea Vera',         role: 'evaluator', isActive: true, isExternal: false, isConfirmed: true,  createdAt: daysFromNow(-120), lastLoginAt: daysFromNow(-5),  userProfile: { researcherType: 'consolidated' } },
  { id: '22222222-2222-4222-8222-000000000016', email: 'j.arriagada@uc.cl',        name: 'Jorge Arriagada',     role: 'evaluator', isActive: true, isExternal: false, isConfirmed: true,  createdAt: daysFromNow(-90),  lastLoginAt: daysFromNow(-12), userProfile: { researcherType: 'consolidated' } },
  { id: '22222222-2222-4222-8222-000000000017', email: 'v.ospina@consultora.cl',   name: 'Valentina Ospina',    role: 'evaluator', isActive: true, isExternal: true,  isConfirmed: true,  createdAt: daysFromNow(-60),  lastLoginAt: daysFromNow(-3),  userProfile: { researcherType: 'emerging' } },
  { id: '22222222-2222-4222-8222-000000000018', email: 'r.pena@uc.cl',             name: 'Rodrigo Peña',        role: 'evaluator', isActive: true, isExternal: false, isConfirmed: false, createdAt: daysFromNow(-30),  userProfile: { researcherType: 'emerging' } },
  { id: '22222222-2222-4222-8222-000000000019', email: 'c.salazar@externa.cl',     name: 'Camila Salazar',      role: 'evaluator', isActive: true, isExternal: true,  isConfirmed: true,  createdAt: daysFromNow(-45),  lastLoginAt: daysFromNow(-8),  userProfile: { researcherType: 'consolidated' } },
  // ── Sin disponibilidad (disponible ≤ 0) ──────────────────────────────────
  {
    id: EV_I,
    email: 'p.alarcon@uc.cl',
    name: 'Pedro Alarcón',
    role: 'evaluator',
    isActive: true,
    isExternal: false,
    isConfirmed: true,
    createdAt: daysFromNow(-90),
    lastLoginAt: daysFromNow(-1),
    userProfile: { researcherType: 'consolidated' },
  },
  {
    id: EV_J,
    email: 's.vidal@consultora-externa.cl',
    name: 'Sofía Vidal',
    role: 'evaluator',
    isActive: true,
    isExternal: true,
    isConfirmed: true,
    createdAt: daysFromNow(-60),
    lastLoginAt: daysFromNow(-2),
    userProfile: { researcherType: 'consolidated' },
  },
  {
    id: EV_K,
    email: 'm.rojas@uc.cl',
    name: 'Matías Rojas',
    role: 'evaluator',
    isActive: true,
    isExternal: false,
    isConfirmed: true,
    createdAt: daysFromNow(-45),
    lastLoginAt: daysFromNow(-4),
    userProfile: { researcherType: 'emerging' },
  },
  // Researchers from other faculties (UA chart variety)
  {
    id: '11111111-1111-4111-8111-000000000009',
    email: 'a.torres@uc.cl',
    name: 'Ana Torres',
    role: 'researcher',
    isActive: true,
    isExternal: false,
    isConfirmed: true,
    createdAt: daysFromNow(-100),
    lastLoginAt: daysFromNow(-3),
    userProfile: {
      run: '11.111.111-9',
      affiliation: 'Pontificia Universidad Católica de Chile',
      facultadUC: 'Ciencias Biológicas',
      unidadAcademica: 'Departamento de Ecología',
      doctorateYear: 2018,
      doctorateMonth: 6,
      researcherType: 'consolidated',
      canApply: true,
    },
  },
  {
    id: '11111111-1111-4111-8111-00000000000A',
    email: 'd.morales@uc.cl',
    name: 'Diego Morales',
    role: 'researcher',
    isActive: true,
    isExternal: false,
    isConfirmed: true,
    createdAt: daysFromNow(-80),
    lastLoginAt: daysFromNow(-5),
    userProfile: {
      run: '11.111.111-A',
      affiliation: 'Pontificia Universidad Católica de Chile',
      facultadUC: 'Historia, Geografía y Ciencia Política',
      unidadAcademica: 'Instituto de Historia',
      doctorateYear: 2015,
      doctorateMonth: 6,
      researcherType: 'consolidated',
      canApply: true,
    },
  },
  {
    id: '11111111-1111-4111-8111-00000000000B',
    email: 'v.perez@uc.cl',
    name: 'Valentina Pérez',
    role: 'researcher',
    isActive: true,
    isExternal: false,
    isConfirmed: true,
    createdAt: daysFromNow(-60),
    lastLoginAt: daysFromNow(-7),
    userProfile: {
      run: '11.111.111-B',
      affiliation: 'Pontificia Universidad Católica de Chile',
      facultadUC: 'Economía y Administración',
      unidadAcademica: 'Departamento de Economía',
      doctorateYear: 2017,
      doctorateMonth: 6,
      researcherType: 'emerging',
      canApply: true,
    },
  },
  {
    id: '11111111-1111-4111-8111-00000000000C',
    email: 's.araya@uc.cl',
    name: 'Sebastián Araya',
    role: 'researcher',
    isActive: true,
    isExternal: false,
    isConfirmed: true,
    createdAt: daysFromNow(-50),
    lastLoginAt: daysFromNow(-10),
    userProfile: {
      run: '11.111.111-C',
      affiliation: 'Pontificia Universidad Católica de Chile',
      facultadUC: 'Química y Farmacia',
      unidadAcademica: 'Escuela de Química y Farmacia',
      doctorateYear: 2019,
      doctorateMonth: 6,
      researcherType: 'emerging',
      canApply: true,
    },
  },
  {
    id: '11111111-1111-4111-8111-00000000000D',
    email: 'c.vasquez@uc.cl',
    name: 'Claudia Vásquez',
    role: 'researcher',
    isActive: true,
    isExternal: false,
    isConfirmed: true,
    createdAt: daysFromNow(-40),
    lastLoginAt: daysFromNow(-14),
    userProfile: {
      run: '11.111.111-D',
      affiliation: 'Pontificia Universidad Católica de Chile',
      facultadUC: 'Ciencias Sociales',
      unidadAcademica: 'Escuela de Psicología',
      doctorateYear: 2021,
      doctorateMonth: 6,
      researcherType: 'emerging',
      canApply: true,
    },
  },
  // Researchers from units with no specific faculty (structural exceptions at UC)
  {
    id: '11111111-1111-4111-8111-00000000000E',
    email: 'r.ibanez@uc.cl',
    name: 'Rodrigo Ibáñez',
    role: 'researcher',
    isActive: true,
    isExternal: false,
    isConfirmed: true,
    createdAt: daysFromNow(-70),
    lastLoginAt: daysFromNow(-4),
    userProfile: {
      run: '11.111.111-E',
      affiliation: 'Pontificia Universidad Católica de Chile',
      // Instituto de Estética is interdisciplinary (Filosofía, Letras y Artes)
      // and does not belong to a single faculty since 2025
      facultadUC: null,
      unidadAcademica: 'Instituto de Estética',
      doctorateYear: 2013,
      doctorateMonth: 6,
      researcherType: 'consolidated',
      canApply: true,
    },
  },
  {
    id: '11111111-1111-4111-8111-00000000000F',
    email: 'p.contreras@uc.cl',
    name: 'Paula Contreras',
    role: 'researcher',
    isActive: true,
    isExternal: false,
    isConfirmed: true,
    createdAt: daysFromNow(-45),
    lastLoginAt: daysFromNow(-6),
    userProfile: {
      run: '11.111.111-F',
      affiliation: 'Pontificia Universidad Católica de Chile',
      // College UC is an independent general-studies unit (no single faculty)
      facultadUC: null,
      unidadAcademica: 'College UC',
      doctorateYear: 2016,
      doctorateMonth: 6,
      researcherType: 'emerging',
      canApply: true,
    },
  },
  // Admins
  {
    id: '33333333-3333-4333-8333-000000000003',
    email: 'admin@test.com',
    name: 'Admin DINV',
    role: 'admin',
    isActive: true,
    isExternal: false,
    isConfirmed: true,
    createdAt: daysFromNow(-300),
    lastLoginAt: now(),
    userProfile: {
      run: '33.333.333-3',
      affiliation: 'TODAS',
    },
  },
  {
    id: '33333333-3333-4333-8333-000000000001',
    email: 'admin.fac_a@test.local',
    name: 'Admin FAC-A',
    role: 'admin',
    isActive: true,
    isExternal: false,
    isConfirmed: true,
    createdAt: daysFromNow(-300),
    userProfile: {
      run: '33.333.333-1',
      affiliation: 'FAC-A',
      facultadUC: 'Ingeniería',
      unidadAcademica: 'Departamento de Ingeniería Eléctrica',
    },
  },
];

// ---------------------------------------------------------------------------
// Form templates — 5 templates sharing the same 7 sections.
// ---------------------------------------------------------------------------

const field = (label: string, type: string, extra: any = {}) => ({
  label,
  type,
  required: false,
  ...extra,
});

// ── Información de la convocatoria: contenido POR CONCURSO ───────────────────
// DEV NOTE (backend): esto era texto fijo del frontend (ContestInformation.tsx)
// y ahora es data del template — cada concurso trae su propio contenido en
// config.contestInfo de APPLICATION_INFORMATION, administrable por la DINV.
// Las "fechas clave" son atributos del Contest (openingDate, closingDate,
// resultsDate, projectStartDate, projectEndDate, finalReportDueDate): acá van
// como texto solo por ser mock; el backend debe derivarlas del Contest.
const buildContestInfo = (
  year: number,
  dates: { label: string; value: string }[]
) => ({
  title: `Concurso de Investigación "Avanza UC ${year}"`,
  intro: [
    'La Pontificia Universidad Católica de Chile, a través de la Dirección de ' +
      'Investigación (DINV) de la Vicerrectoría de Investigación y Postgrado ' +
      `(VRIP), invita a su cuerpo académico a participar en el **Concurso de ` +
      `Investigación "Avanza UC ${year}"**. Esta iniciativa forma ` +
      'parte de la Estrategia UC 2026-2030 y tiene como objetivo impulsar ' +
      'proyectos audaces, de frontera, de alto riesgo e impacto, en las artes, ' +
      'humanidades, ciencias y tecnologías, fomentando la colaboración ' +
      'interdisciplinaria y la proyección global de la investigación.',
    'El concurso busca apoyar propuestas que aborden **desafíos estratégicos ' +
      'para Chile** con potencial de trascender a escala internacional, ' +
      'sirviendo además como **fondos semilla** para atraer nuevos recursos y ' +
      'talento de investigadores y estudiantes nacionales y extranjeros.',
  ],
  sections: [
    {
      title: 'Objetivo general del concurso',
      paragraphs: ['Financiar proyectos de investigación que:'],
      items: [
        { text: 'Generen conocimiento básico y/o aplicado en áreas prioritarias para Chile y de interés global.' },
        { text: 'Exploren ámbitos de frontera con alto riesgo académico y alto impacto potencial.' },
        { text: 'Articulen colaboraciones interdisciplinarias e internacionales.' },
        { text: 'Contribuyan a la formación de nuevas redes y a la atracción de recursos externos.' },
      ],
    },
    {
      title: 'A quién está dirigido',
      paragraphs: [
        'Podrán postular académicas y académicos de la UC con contrato de ' +
          '**33 horas semanales**, pertenecientes a la planta ordinaria o ' +
          'especial, en calidad de Investigador Responsable, Investigador ' +
          'Principal y/o Investigador Asociado. Existen dos categorías de ' +
          'concurso:',
      ],
      items: [
        {
          text:
            '**Categoría A — Emergente:** IR o Investigadores Principales (IP) ' +
            'en categoría Asistente que hayan obtenido su grado de doctor o ' +
            'subespecialidad médica **desde enero de 2020 en adelante**.',
        },
        {
          text:
            '**Categoría B — Consolidado:** IR o IP en cualquier categoría ' +
            'académica (Asistente, Asociado, Titular) que hayan obtenido su ' +
            'grado de doctor o subespecialidad médica **antes de enero de ' +
            '2020**. Si al menos un IR o IP del equipo es Consolidado, el ' +
            'proyecto compite en Categoría B.',
        },
      ],
      paragraphsAfter: [
        'El equipo puede incluir hasta **4 académicos UC** (IR + Investigadores ' +
          'Principales (IP) + Investigadores Asociados (IA)) y ' +
          'co-investigadores externos (COI) sin límite de cantidad. Cada ' +
          'académico UC puede participar en un máximo de **2 proyectos** en ' +
          'cualquier rol (IR, IP o IA).',
      ],
    },
    {
      title: 'Duración y financiamiento',
      items: [
        { text: '**Duración:** Hasta 18 meses.' },
        { text: '**Monto máximo adjudicable por proyecto:** $36.000.000.' },
        {
          text:
            'Los recursos podrán destinarse a personal de apoyo, movilidad, ' +
            'cooperación nacional e internacional, gastos de operación y bienes ' +
            'de capital, de acuerdo con lo establecido en las Bases.',
        },
      ],
    },
    {
      title: 'Fechas clave',
      dates,
    },
    {
      title: 'Recomendaciones',
      paragraphs: ['Antes de iniciar su postulación, se sugiere:'],
      items: [
        {
          text:
            'Leer detenidamente las bases del concurso: ' +
            '[Bases en Español](/templates/Bases_Avanza_UC.pdf) · ' +
            '[Bases en Inglés](/templates/Bases_Avanza_UC_EN.pdf)',
        },
        {
          text:
            'Revisar el material de apoyo: ' +
            '[Preguntas Frecuentes](/templates/Preguntas_Frecuentes_AvanzaUC.docx) · ' +
            '[Glosario](/templates/Glosario_AvanzaUC.docx)',
        },
        {
          text: 'Preparar con anticipación los documentos requeridos:',
          children: [
            { text: 'Resumen Ejecutivo (se completa en el formulario)' },
            {
              text:
                'Documento del Proyecto (Hipótesis, Metodología, Pertinencia ' +
                'nacional, Transferibilidad global, Resultados comprometidos, ' +
                'Plan de trabajo anual)',
              links: [
                { label: 'Descargar plantilla (Español)', url: '/templates/Avanza_UC_Formulacion Proyecto.docx' },
                { label: 'Descargar plantilla (English)', url: '/templates/Avanza_UC_Research_Proposal_EN.docx' },
              ],
            },
            {
              text: 'Presupuesto (Excel)',
              links: [
                { label: 'Descargar plantilla del presupuesto', url: '/templates/Presupuesto_AvanzaUC2026.xlsx' },
              ],
            },
            { text: 'CV narrativo (se completa en el formulario)' },
            {
              text: 'Declaraciones Obligatorias Internas (DOI)',
              links: [
                { label: 'Descargar plantilla DOI para Investigador Responsable', url: '/templates/FORMATO DOI IR.docx' },
                { label: 'Descargar plantilla DOI para Investigador Principal (si aplica)', url: '/templates/FORMATO DOI IP.docx' },
                { label: 'Descargar plantilla DOI para Investigador Asociado (si aplica)', url: '/templates/FORMATO DOI IA.docx' },
              ],
            },
            {
              text: 'Cartas de compromiso',
              links: [
                { label: 'Descargar plantilla (Español)', url: '/templates/CARTA COI COMPROMISO SPA.docx' },
                { label: 'Descargar plantilla (English)', url: '/templates/CARTA COI COMPROMISO ENG.docx' },
              ],
            },
            {
              text: 'Cartas de apoyo',
              links: [
                { label: 'Descargar plantilla (Español)', url: '/templates/CARTA APOYO SPA.docx' },
                { label: 'Descargar plantilla (English)', url: '/templates/CARTA APOYO ENG.docx' },
              ],
            },
          ],
        },
        {
          text:
            'Asegurarse de contar con el apoyo formal de la Decana o Decano de ' +
            'la unidad académica correspondiente, manifestada a través de la ' +
            'DOI para cada académico/a UC.',
        },
        { text: 'No dejar el envío de la postulación para el último día de cierre.' },
      ],
    },
    {
      title: 'Importante',
      items: [
        { text: 'Solo se aceptarán postulaciones ingresadas a través de la plataforma oficial de postulación en línea.' },
        {
          text:
            'La plataforma contempla validaciones previas al envío; asegúrese ' +
            'de realizar este proceso con la debida anticipación y en ' +
            'condiciones de conexión seguras.',
        },
        { text: 'Una vez cerrado el concurso, no se recibirán postulaciones ni documentos anexos por otras vías.' },
      ],
    },
    {
      title: 'Consultas',
      tone: 'highlight' as const,
      paragraphs: [
        'Las consultas deberán realizarse a la Coordinación del Concurso de la ' +
          'Dirección de Investigación, al correo electrónico: **avanza.uc@uc.cl**.',
      ],
    },
  ],
});

// Fechas clave por concurso (mock; en backend salen del Contest).
const CONTEST_KEY_DATES: Record<number, { label: string; value: string }[]> = {
  2026: [
    { label: 'Apertura:', value: '30 de junio de 2026' },
    { label: 'Cierre:', value: '30 de septiembre de 2026' },
    { label: 'Resultados:', value: '21 de diciembre de 2026' },
    { label: 'Inicio de proyectos:', value: '12 de abril de 2027' },
    { label: 'Término de proyectos:', value: '16 de octubre de 2028' },
    { label: 'Entrega de informe final:', value: 'Por confirmar' },
  ],
  2027: [
    { label: 'Apertura:', value: '8 de agosto de 2026' },
    { label: 'Cierre:', value: '8 de septiembre de 2026' },
    { label: 'Resultados:', value: 'Por confirmar' },
    { label: 'Inicio de proyectos:', value: 'Por confirmar' },
    { label: 'Término de proyectos:', value: 'Por confirmar' },
    { label: 'Entrega de informe final:', value: 'Por confirmar' },
  ],
  2025: [
    { label: 'Apertura:', value: '9 de abril de 2026' },
    { label: 'Cierre:', value: '8 de julio de 2026' },
    { label: 'Resultados:', value: 'Concurso cerrado' },
  ],
  2024: [
    { label: 'Apertura:', value: '10 de abril de 2025' },
    { label: 'Cierre:', value: '11 de enero de 2026' },
    { label: 'Resultados:', value: 'Concurso cerrado' },
  ],
};

// Inyecta el contestInfo del año en la sección APPLICATION_INFORMATION,
// clonando solo esa sección (las demás se comparten entre templates).
const withContestInfo = (sections: any[], year: number) =>
  sections.map((s) =>
    s.sectionType === 'APPLICATION_INFORMATION'
      ? {
          ...s,
          config: {
            ...s.config,
            contestInfo: buildContestInfo(year, CONTEST_KEY_DATES[year] ?? []),
          },
        }
      : s
  );

const buildSections = () => [
  {
    id: 'sec-1',
    title: 'Convocatoria',
    sectionType: 'APPLICATION_INFORMATION' as SectionType,
    order: 1,
    config: {
      description: 'Datos generales de la postulación.',
      fields: {
        title: field('Título', 'text', { required: true, minLength: 5, maxLength: 200 }),
        contact_email: field('Email de contacto', 'email', { required: true }),
      },
    },
  },
  {
    id: 'sec-2',
    title: 'Antecedentes',
    sectionType: 'PROJECT_INFORMATION' as SectionType,
    order: 2,
    config: {
      description: 'Antecedentes del proyecto de investigación.',
      fields: {
        short_description: field('Descripción breve', 'textarea', { required: true, maxLength: 2000 }),
        duration_months: field('Duración (meses)', 'number', { required: true, min: 6, max: 36 }),
        discipline: field('Disciplina', 'select', {
          required: true,
          options: ['biology', 'chemistry', 'engineering', 'humanities', 'social'],
        }),
        keywords: field('Palabras clave', 'array', { minItems: 3, maxItems: 8 }),
        is_interdisciplinary: field('¿Es interdisciplinario?', 'checkbox'),
      },
    },
  },
  {
    id: 'sec-3',
    title: 'Formulación',
    sectionType: 'PROJECT_DESCRIPTION' as SectionType,
    order: 3,
    config: {
      description: 'Documento de formulación del proyecto.',
      fields: {
        project_description: field('Documento del proyecto', 'file', {
          required: true,
          acceptedTypes: [PDF],
          maxFileSize: 10485760,
        }),
      },
    },
  },
  {
    id: 'sec-4',
    title: 'Equipo',
    sectionType: 'PROJECT_TEAM' as SectionType,
    order: 4,
    config: {
      description: 'Integrantes del equipo de investigación.',
      fields: {
        members: field('Integrantes', 'array', { minItems: 1, maxItems: 10 }),
      },
    },
  },
  {
    id: 'sec-5',
    title: 'Recursos',
    sectionType: 'PROJECT_RESOURCES' as SectionType,
    order: 5,
    config: {
      description: 'Presupuesto y recursos solicitados.',
      // maxAmountCLP → Contest.config.maxAmountCLP (valor configurable por concurso)
      // En producción debe leerse desde la configuración del concurso (no hard-codear aquí).
      maxAmountCLP: 36_000_000,
      fields: {
        budget_excel: field('Presupuesto (Excel)', 'file', {
          required: true,
          acceptedTypes: [XLSX],
          maxFileSize: 10485760,
        }),
        monto_solicitado: field('Monto solicitado a Avanza UC (CLP)', 'number', { required: true, min: 0 }),
        monto_contrapartes: field('Monto aportado por contrapartes (CLP)', 'number', { required: true, min: 0 }),
      },
    },
  },
  {
    id: 'sec-6',
    title: 'Adjuntos',
    sectionType: 'PROJECT_ATTACHMENTS' as SectionType,
    order: 6,
    config: {
      description: 'Publicaciones y anexos.',
      fields: {
        publications: field('Publicaciones', 'fileArray', {
          minItems: 5,
          maxItems: 5,
          acceptedTypes: [PDF],
          maxFileSize: 10485760,
        }),
        dois: field('DOIs', 'array', { minItems: 5, maxItems: 5 }),
      },
    },
  },
  {
    id: 'sec-7',
    title: 'Envío',
    sectionType: 'PROJECT_SUMMARY' as SectionType,
    order: 7,
    config: {
      description: 'Revisión final y envío.',
      fields: {
        summary_readonly: field('Resumen', 'readonly'),
      },
    },
  },
];

const buildFormTemplates = () => {
  const sections = buildSections();
  return [
    {
      id: '44444444-4444-4444-8444-000000000001',
      name: 'Avanza UC',
      description: 'Concurso de adjudicación de fondos para proyectos de investigación.',
      isActive: true,
      isPublished: true,
      year: 2026,
      openDate: '2026-06-30T00:00:00.000Z',
      deadline: '2026-09-30T23:59:59.000Z',
      publicDeadline: '2026-09-30T23:59:59.000Z',
      sections: withContestInfo(sections, 2026),
    },
    {
      id: '44444444-4444-4444-8444-000000000003',
      name: 'Avanza UC',
      description: 'Concurso de adjudicación de fondos para proyectos de investigación.',
      isActive: true,
      isPublished: true,
      year: 2025,
      openDate: daysFromNow(-91),
      deadline: daysFromNow(-1),
      publicDeadline: daysFromNow(-1),
      sections: withContestInfo(sections, 2025),
    },
    {
      id: '44444444-4444-4444-8444-000000000005',
      name: 'Avanza UC',
      description: 'Concurso de adjudicación de fondos para proyectos de investigación.',
      isActive: true,
      isPublished: false,
      year: 2027,
      openDate: daysFromNow(30),
      deadline: daysFromNow(60),
      publicDeadline: daysFromNow(60),
      sections: withContestInfo(sections, 2027),
    },
    {
      id: '44444444-4444-4444-8444-000000000006',
      name: 'Avanza UC',
      description: 'Concurso de adjudicación de fondos para proyectos de investigación.',
      isActive: false,
      isPublished: true,
      year: 2024,
      openDate: daysFromNow(-455),
      deadline: daysFromNow(-180),
      publicDeadline: daysFromNow(-180),
      sections: withContestInfo(sections, 2024),
    },
  ];
};

// ---------------------------------------------------------------------------
// Rich static-shaped answers (so the form shows real content).
// ---------------------------------------------------------------------------

const principalInvestigator = (overrides: any = {}) => ({
  name: 'María González',
  email: 'investigador@test.com',
  run: '11.111.111-1',
  documentType: 'run',
  affiliation: 'Pontificia Universidad Católica de Chile',
  facultadUC: 'Ingeniería',
  unidadAcademicaUC: 'Departamento de Ingeniería Eléctrica',
  doctorateYear: 2020,
  doctorateMonth: 6,
  type: 'emerging',
  gender: 'F',
  role: 'IR',
  institutionType: 'Nacional',
  country: 'Chile',
  ...overrides,
});

// BACKEND NOTE (REP-INST): Each Researcher must carry a `country` field (see types/index.ts).
// The institution-by-country report depends entirely on it. International COIs below use
// a specific country name; domestic ones use 'Chile'. The backend must enforce `country`
// is always present and non-empty when the researcher record is persisted.
const intlCOI = (
  name: string, email: string, affiliation: string, country: string, doctorateYear = 2010, doctorateMonth = 6, gender: 'M' | 'F' = 'M'
): any => ({
  name, email, run: '', documentType: 'passport', affiliation,
  doctorateYear, doctorateMonth, type: 'consolidated', gender, role: 'COI',
  institutionType: 'Internacional', country,
});

// Maps the area name used in seed calls to OCDE numeric IDs (string)
const ocdeAreaMap: Record<string, { areaId: string; subareaId: string }> = {
  'Ciencias Naturales':          { areaId: '2',  subareaId: '67' },  // BIOLOGICAS
  'Ciencias Sociales':           { areaId: '4',  subareaId: '73' },  // SOCIOLOGIA
  'Ciencias Médicas y de Salud': { areaId: '3',  subareaId: '69' },  // MEDICINA BASICA
  'Ciencias de la Salud':        { areaId: '3',  subareaId: '70' },  // CIENCIAS DE LA SALUD
  'Ciencias Agrícolas':          { areaId: '1',  subareaId: '58' },  // AGRICULTURA
  'Ingeniería y Tecnología':     { areaId: '5',  subareaId: '78' },  // INGENIERIA ELECTRICA/INFORMATICA
};

const richAnswers = (
  title: string,
  oecdAreaKey: string = 'Ciencias Naturales',
  extraCOIs: any[] = [],
  montoSolicitado = 25_000_000,
  montoContrapartes = 5_000_000,
  // Adjuntos opcionales (archivo de presupuesto, CV/publicaciones de IR+IP,
  // cartas de apoyo institucional). Retrocompatible: por defecto todo vacío.
  extras: { budgetFile?: string; ipCvData?: any[]; institutionLetters?: string[] } = {}
) => {
  const ocde = ocdeAreaMap[oecdAreaKey] ?? ocdeAreaMap['Ciencias Naturales'];
  return ({
  PROJECT_INFORMATION: {
    projectTitle: title,
    interdisciplinaryProject: 'SI',
    transdisciplinaryProject: 'NO',
    genderApproachProject: 'NO',
    genderApproachOptions: [],
    openScience: 'SI',
    rcer: [],
    keywords: 'neurociencia;aprendizaje;plasticidad',
    evaluationArea: 'Neurociencia del aprendizaje y plasticidad cerebral',
    oecdArea: ocde.areaId,
    oecdSubarea: ocde.subareaId,
    oecdDiscipline: '',
    sdgGoals: [],
    executionRegions: ['Región Metropolitana'],
    orcid: '0000-0002-1825-0097',
  },
  PROJECT_DESCRIPTION: {
    executiveSummary:
      'Este proyecto busca caracterizar los mecanismos de plasticidad sináptica ' +
      'asociados al aprendizaje en modelos murinos, integrando técnicas de imagenología ' +
      'avanzada y análisis computacional para identificar marcadores tempranos de adaptación neuronal.',
  },
  PROJECT_TEAM: {
    principalInvestigator: principalInvestigator(),
    coInvestigators: [
      {
        name: 'Roberto Rodríguez',
        email: 'r.rodriguez@uc.cl',
        run: '12.345.678-9',
        documentType: 'run',
        affiliation: 'Pontificia Universidad Católica de Chile',
        facultadUC: 'Ciencias Biológicas',
        doctorateYear: 2015,
        doctorateMonth: 6,
        type: 'consolidated',
        gender: 'M',
        role: 'IP',
        institutionType: 'Nacional',
        country: 'Chile',
      },
      {
        name: 'Camila Vargas',
        email: 'c.vargas@uc.cl',
        run: '13.456.789-0',
        documentType: 'run',
        affiliation: 'Pontificia Universidad Católica de Chile',
        facultadUC: 'Medicina',
        unidadAcademicaUC: 'Departamento de Fisiología',
        type: 'emerging',
        gender: 'F',
        role: 'IA',
        institutionType: 'Nacional',
        country: 'Chile',
      },
      ...extraCOIs,
    ],
  },
  PROJECT_RESOURCES: {
    budget: { detailedFile: extras.budgetFile, montoSolicitado, montoContrapartes },
  },
  PROJECT_ATTACHMENTS: {
    hasConflictOfInterest: 'NO',
    conflictOfInterestList: [],
    suggestedReviewers: [],
    ipCvData: extras.ipCvData ?? [],
    institutionCommitmentLetters: extras.institutionLetters ?? [],
  },
  })
};

const allSectionsCompleted: Partial<Record<SectionType, SectionStatus>> = {
  APPLICATION_INFORMATION: 'completed',
  PROJECT_INFORMATION: 'completed',
  PROJECT_DESCRIPTION: 'completed',
  PROJECT_TEAM: 'completed',
  PROJECT_RESOURCES: 'completed',
  PROJECT_ATTACHMENTS: 'completed',
  PROJECT_SUMMARY: 'completed',
};

// ---------------------------------------------------------------------------
// Applications
// ---------------------------------------------------------------------------

const T_OPEN = '44444444-4444-4444-8444-000000000001';
const T_CLOSED = '44444444-4444-4444-8444-000000000003';

const U_BASIC = '11111111-1111-4111-8111-000000000001';
const U_OTHER = '11111111-1111-4111-8111-000000000007';
const ADMIN_DINV = '33333333-3333-4333-8333-000000000003';

// New researchers (UA variety)
const U_BIOL     = '11111111-1111-4111-8111-000000000009';   // Ciencias Biológicas
const U_HIST     = '11111111-1111-4111-8111-00000000000A';   // Historia, Geografía y Ciencia Política
const U_ECO      = '11111111-1111-4111-8111-00000000000B';   // Economía y Administración
const U_QUI      = '11111111-1111-4111-8111-00000000000C';   // Química y Farmacia
const U_CS       = '11111111-1111-4111-8111-00000000000D';   // Ciencias Sociales
// Units with no single parent faculty (facultadUC: null)
const U_ESTETICA = '11111111-1111-4111-8111-00000000000E';   // Instituto de Estética (interdisciplinario)
const U_COLLEGE  = '11111111-1111-4111-8111-00000000000F';   // College UC (independiente)

const APP_UNDER_REVIEW = '55555555-5555-4555-8555-000000000006';

// CV narrativo + publicaciones (DOI) de los Investigadores Principales (IR + IP)
// de AV26006. Alimenta la sección "Publicaciones y CV" del panel del evaluador.
// Los archivos DOI se generan en buildFiles (file-006-doi-ir / -ip).
const AV26006_IP_CV = [
  {
    investigatorName: 'María González',
    cvNarrative:
      'Investigadora en neurociencia computacional, con foco en imagenología de la ' +
      'actividad neuronal y modelamiento de circuitos de aprendizaje. Ha liderado ' +
      'proyectos que integran registro in vivo de calcio con análisis de series de ' +
      'tiempo a gran escala. Su trabajo reciente desarrolla marcadores tempranos de ' +
      'plasticidad a partir de datos de microscopía de dos fotones en modelos murinos ' +
      'de memoria, combinando técnicas de ingeniería de señales y neurociencia experimental.',
    publications: [
      {
        title:
          'Marcadores tempranos de plasticidad sináptica en imágenes de calcio de dos fotones',
        publishedAt: '2024-03-15',
        identifierType: 'DOI',
        identifierValue: '10.1523/JNEUROSCI.2143-23.2024',
        fileId: 'file-006-pub-ir-1',
      },
      {
        title:
          'Descomposición de series de tiempo de actividad neuronal a gran escala en modelos murinos de memoria',
        publishedAt: '2023-06-22',
        identifierType: 'DOI',
        identifierValue: '10.1016/j.neuroimage.2023.120145',
        fileId: 'file-006-pub-ir-2',
      },
      {
        title:
          'Dinámica de ensambles hipocampales durante la consolidación de memoria espacial',
        publishedAt: '2022-11-03',
        identifierType: 'DOI',
        identifierValue: '10.1038/s41593-022-01198-z',
        fileId: 'file-006-pub-ir-3',
      },
      {
        title:
          'Modelo computacional de circuitos de aprendizaje con registro in vivo de calcio',
        publishedAt: '2023-02-09',
        identifierType: 'DOI',
        identifierValue: '10.1371/journal.pcbi.1010892',
        fileId: 'file-006-pub-ir-4',
      },
      {
        title:
          'Reducción de ruido en microscopía de dos fotones mediante filtrado adaptativo',
        publishedAt: '2021-08-17',
        identifierType: 'DOI',
        identifierValue: '10.1109/TMI.2021.3098765',
        fileId: 'file-006-pub-ir-5',
      },
    ],
    doiFile: 'file-006-doi-ir',
  },
  {
    investigatorName: 'Roberto Rodríguez',
    cvNarrative:
      'Biólogo especializado en mecanismos moleculares de plasticidad sináptica y ' +
      'potenciación de largo plazo (LTP). Ha caracterizado vías de señalización ' +
      'dependientes de receptores NMDA en el hipocampo y su rol en la consolidación ' +
      'de la memoria. Mantiene una línea consolidada de publicaciones en revistas de ' +
      'alto impacto y amplia experiencia en electrofisiología y biología molecular.',
    publications: [
      {
        title:
          'Señalización dependiente de receptores NMDA en la consolidación de memoria de largo plazo',
        publishedAt: '2023-10-05',
        identifierType: 'DOI',
        identifierValue: '10.1038/s41586-023-06789-9',
        fileId: 'file-006-pub-ip-1',
      },
      {
        title:
          'Potenciación de largo plazo en sinapsis CA3-CA1: rol de las quinasas CaMKII',
        publishedAt: '2022-10-19',
        identifierType: 'DOI',
        identifierValue: '10.1016/j.neuron.2022.09.018',
        fileId: 'file-006-pub-ip-2',
      },
      {
        title:
          'Reconfiguración molecular de espinas dendríticas durante el aprendizaje asociativo',
        publishedAt: '2022-05-13',
        identifierType: 'DOI',
        identifierValue: '10.1126/science.abn4705',
        fileId: 'file-006-pub-ip-3',
      },
      {
        title:
          'Electrofisiología de circuitos hipocampales: protocolos de registro en rodajas',
        publishedAt: '2023-01-26',
        identifierType: 'DOI',
        identifierValue: '10.1113/JP283456',
        fileId: 'file-006-pub-ip-4',
      },
      {
        title:
          'Vías de señalización sináptica y su modulación por experiencia temprana',
        publishedAt: '2022-07-08',
        identifierType: 'DOI',
        identifierValue: '10.1073/pnas.2201234119',
        fileId: 'file-006-pub-ip-5',
      },
    ],
    doiFile: 'file-006-doi-ip',
  },
];

const buildApplications = (): StoredApplication[] => [
  {
    id: '55555555-5555-4555-8555-000000000001',
    userId: U_BASIC,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26001',
    status: 'draft',
    sectionAnswers: {},
    sectionStatus: {},
    lastSaved: daysFromNow(-1),
    complianceConfirmed: false,
    submittedAt: null,
    createdAt: daysFromNow(-3),
    updatedAt: daysFromNow(-1),
    awardNotifiedAt: null,
    statusHistory: [],
  },
  {
    id: '55555555-5555-4555-8555-000000000002',
    userId: U_BASIC,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26002',
    status: 'draft',
    sectionAnswers: {
      PROJECT_INFORMATION: {
        projectTitle: 'Proyecto Parcial en Energías Renovables',
        keywords: 'energía;solar',
      },
      PROJECT_DESCRIPTION: {
        executiveSummary: 'Una descripción corta del proyecto en progreso.',
      },
    },
    sectionStatus: {
      PROJECT_INFORMATION: 'in_progress',
      PROJECT_DESCRIPTION: 'in_progress',
    },
    lastSaved: daysFromNow(-2),
    complianceConfirmed: false,
    submittedAt: null,
    createdAt: daysFromNow(-6),
    updatedAt: daysFromNow(-2),
    awardNotifiedAt: null,
    statusHistory: [],
  },
  {
    id: '55555555-5555-4555-8555-000000000004',
    userId: U_BASIC,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26004',
    status: 'submitted',
    sectionAnswers: {
      ...richAnswers('Plataforma integral de monitoreo ambiental inteligente mediante redes de sensores IoT distribuidos y modelos predictivos de aprendizaje automático para la gestión sostenible de recursos hídricos en cuencas altoandinas', 'Ingeniería y Tecnología'),
      PROJECT_TEAM: { principalInvestigator: principalInvestigator(), coInvestigators: [] },
    },
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-2),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-2),
    createdAt: daysFromNow(-12),
    updatedAt: daysFromNow(-2),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-2) },
    statusHistory: [
      {
        status: 'submitted',
        changedBy: U_BASIC,
        changedByName: 'María González',
        changedAt: daysFromNow(-2),
        notes: 'Postulación enviada por el investigador.',
      },
    ],
  },
  {
    id: '55555555-5555-4555-8555-000000000005',
    userId: U_OTHER,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26005',
    status: 'submitted',
    sectionAnswers: {
      ...richAnswers('Inclusión Digital en Comunidades Rurales', 'Ciencias Sociales'),
      PROJECT_TEAM: {
        principalInvestigator: principalInvestigator(),
        coInvestigators: [
          { name: 'Andrés Vera', email: 'a.vera@udec.cl', run: '16.543.210-K', documentType: 'run',
            affiliation: 'Universidad de Concepción', doctorateYear: 2018, doctorateMonth: 6, type: 'emerging', gender: 'M',
            role: 'COI', institutionType: 'Nacional', country: 'Chile' },
        ],
      },
    },
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-2),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-2),
    createdAt: daysFromNow(-12),
    updatedAt: daysFromNow(-2),
    awardNotifiedAt: null,
    statusHistory: [
      {
        status: 'submitted',
        changedBy: U_OTHER,
        changedByName: 'Carolina Rivas',
        changedAt: daysFromNow(-2),
      },
    ],
  },
  {
    id: APP_UNDER_REVIEW,
    userId: U_BASIC,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26006',
    status: 'under_evaluation',
    sectionAnswers: richAnswers('Mecanismos de plasticidad sináptica y consolidación de la memoria en modelos murinos: integración de registro in vivo de calcio, microscopía de dos fotones y análisis computacional de series de tiempo a gran escala', 'Ciencias Naturales', [
      { name: 'Felipe Muñoz', email: 'f.munoz@udechile.cl', run: '16.789.012-3', documentType: 'run',
        affiliation: 'Universidad de Chile', doctorateYear: 2018, doctorateMonth: 4, type: 'emerging', gender: 'M', role: 'COI',
        institutionType: 'Nacional', country: 'Chile' },
      intlCOI('Claudia Stern', 'c.stern@neuro.mpg.de', 'Max Planck Institute for Brain Research', 'Alemania', 2011, 3, 'F'),
    ], 25_000_000, 5_000_000, {
      budgetFile: 'file-006-budget',
      ipCvData: AV26006_IP_CV,
      institutionLetters: ['file-006-pub1'],
    }),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-5),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-5),
    createdAt: daysFromNow(-15),
    updatedAt: daysFromNow(-3),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-5) },
    statusHistory: [
      {
        status: 'submitted',
        changedBy: U_BASIC,
        changedByName: 'María González',
        changedAt: daysFromNow(-5),
        notes: 'Postulación enviada.',
      },
      {
        status: 'admissible',
        changedBy: ADMIN_DINV,
        changedByName: 'Admin DINV',
        changedAt: daysFromNow(-4),
        notes: 'Cumple requisitos de admisibilidad.',
      },
      {
        status: 'under_evaluation',
        changedBy: ADMIN_DINV,
        changedByName: 'Admin DINV',
        changedAt: daysFromNow(-3),
        notes: 'Asignada a evaluadores.',
      },
    ],
  },
  {
    id: '55555555-5555-4555-8555-000000000007',
    userId: U_BASIC,
    formTemplateId: T_CLOSED,
    applicationNumber: 'AV25004',
    status: 'selected',
    sectionAnswers: richAnswers('Biomateriales Sostenibles para Regeneración Ósea', 'Ciencias Médicas y de Salud', [
      intlCOI('James Wilson', 'j.wilson@mit.edu', 'Massachusetts Institute of Technology', 'Estados Unidos', 2005),
    ]),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-395),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-395),
    createdAt: daysFromNow(-410),
    updatedAt: daysFromNow(-370),
    awardNotifiedAt: daysFromNow(-370),
    statusHistory: [
      {
        status: 'submitted',
        changedBy: U_BASIC,
        changedByName: 'María González',
        changedAt: daysFromNow(-395),
        notes: 'Postulación enviada por el investigador.',
      },
      {
        status: 'under_evaluation',
        changedBy: ADMIN_DINV,
        changedByName: 'Admin DINV',
        changedAt: daysFromNow(-390),
        notes: 'Pasa a evaluación.',
      },
      {
        status: 'evaluated',
        changedBy: ADMIN_DINV,
        changedByName: 'Admin DINV',
        changedAt: daysFromNow(-380),
      },
      {
        status: 'selected',
        changedBy: ADMIN_DINV,
        changedByName: 'Admin DINV',
        changedAt: daysFromNow(-370),
        notes: 'Seleccionado para financiamiento.',
      },
    ],
  },
  {
    id: '55555555-5555-4555-8555-000000000008',
    userId: U_BASIC,
    formTemplateId: T_CLOSED,
    applicationNumber: 'AV25005',
    status: 'not_selected',
    sectionAnswers: richAnswers('Optimización Logística mediante Aprendizaje Profundo', 'Ingeniería y Tecnología', [
      intlCOI('Klaus Müller', 'k.mueller@tu-berlin.de', 'Technische Universität Berlin', 'Alemania', 2008),
    ]),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-393),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-393),
    createdAt: daysFromNow(-408),
    updatedAt: daysFromNow(-368),
    awardNotifiedAt: daysFromNow(-368),
    statusHistory: [
      {
        status: 'submitted',
        changedBy: U_BASIC,
        changedByName: 'María González',
        changedAt: daysFromNow(-393),
        notes: 'Postulación enviada.',
      },
      {
        status: 'under_evaluation',
        changedBy: ADMIN_DINV,
        changedByName: 'Admin DINV',
        changedAt: daysFromNow(-388),
      },
      {
        status: 'not_selected',
        changedBy: ADMIN_DINV,
        changedByName: 'Admin DINV',
        changedAt: daysFromNow(-368),
        notes: 'No fue seleccionado en este proceso.',
      },
    ],
  },
  {
    id: '55555555-5555-4555-8555-00000000000A',
    userId: U_BASIC,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26010',
    status: 'draft',
    sectionAnswers: {},
    sectionStatus: {},
    lastSaved: daysFromNow(-1),
    complianceConfirmed: false,
    submittedAt: null,
    createdAt: daysFromNow(-2),
    updatedAt: daysFromNow(-1),
    awardNotifiedAt: null,
    statusHistory: [],
  },
  {
    id: '55555555-5555-4555-8555-000000000009',
    userId: U_OTHER,
    formTemplateId: T_CLOSED,
    applicationNumber: 'AV25003',
    status: 'submitted',
    sectionAnswers: richAnswers('Conservación de Humedales Costeros', 'Ciencias Agrícolas'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-400),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-400),
    createdAt: daysFromNow(-415),
    updatedAt: daysFromNow(-400),
    awardNotifiedAt: null,
    statusHistory: [
      {
        status: 'submitted',
        changedBy: U_OTHER,
        changedByName: 'Carolina Rivas',
        changedAt: daysFromNow(-3),
      },
    ],
  },

  // ── under_admissibility_review (3) ─────────────────────────────────────────
  {
    id: '55555555-5555-4555-8555-00000000000D',
    userId: '11111111-1111-4111-8111-000000000006',
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26011',
    status: 'under_admissibility_review',
    sectionAnswers: richAnswers('Biodiversidad y Cambio Climático en Ecosistemas Altoandinos', 'Ciencias Naturales'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-9),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-9),
    createdAt: daysFromNow(-18),
    updatedAt: daysFromNow(-7),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-7) },
    statusHistory: [
      { status: 'submitted', changedBy: '11111111-1111-4111-8111-000000000006', changedByName: 'Jorge Fuentes', changedAt: daysFromNow(-9) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-7), notes: 'Revisor asignado.' },
    ],
  },
  {
    id: '55555555-5555-4555-8555-00000000000E',
    userId: U_BASIC,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26012',
    status: 'under_admissibility_review',
    sectionAnswers: richAnswers('Nanotecnología Aplicada a la Purificación de Agua', 'Ingeniería y Tecnología'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-8),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-8),
    createdAt: daysFromNow(-16),
    updatedAt: daysFromNow(-6),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-6) },
    statusHistory: [
      { status: 'submitted', changedBy: U_BASIC, changedByName: 'María González', changedAt: daysFromNow(-8) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-6) },
    ],
  },
  {
    id: '55555555-5555-4555-8555-00000000000F',
    userId: U_OTHER,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26013',
    status: 'under_admissibility_review',
    sectionAnswers: richAnswers('Salud Mental en Adolescentes Post-Pandemia', 'Ciencias Médicas y de Salud'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-7),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-7),
    createdAt: daysFromNow(-14),
    updatedAt: daysFromNow(-5),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-5) },
    statusHistory: [
      { status: 'submitted', changedBy: U_OTHER, changedByName: 'Carolina Rivas', changedAt: daysFromNow(-7) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-5) },
    ],
  },

  // ── not_admissible (3) ─────────────────────────────────────────────────────
  {
    id: '55555555-5555-4555-8555-000000000010',
    userId: '11111111-1111-4111-8111-000000000006',
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26014',
    status: 'not_admissible',
    sectionAnswers: richAnswers('Robots Colaborativos en Manufactura Avanzada', 'Ingeniería y Tecnología'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-14),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-14),
    createdAt: daysFromNow(-22),
    updatedAt: daysFromNow(-10),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-12) },
    statusHistory: [
      { status: 'submitted', changedBy: '11111111-1111-4111-8111-000000000006', changedByName: 'Jorge Fuentes', changedAt: daysFromNow(-14) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-12) },
      { status: 'not_admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-10), notes: 'No cumple requisitos de afiliación institucional.' },
    ],
  },
  {
    id: '55555555-5555-4555-8555-000000000011',
    userId: U_BASIC,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26015',
    status: 'not_admissible',
    sectionAnswers: richAnswers('Economía Circular en la Industria Minera', 'Ciencias Sociales'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-13),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-13),
    createdAt: daysFromNow(-20),
    updatedAt: daysFromNow(-9),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-11) },
    statusHistory: [
      { status: 'submitted', changedBy: U_BASIC, changedByName: 'María González', changedAt: daysFromNow(-13) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-11) },
      { status: 'not_admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-9), notes: 'Documentación incompleta.' },
    ],
  },
  {
    id: '55555555-5555-4555-8555-000000000012',
    userId: U_OTHER,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26016',
    status: 'not_admissible',
    sectionAnswers: richAnswers('Microbioma Intestinal y Enfermedades Autoinmunes', 'Ciencias Médicas y de Salud'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-12),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-12),
    createdAt: daysFromNow(-19),
    updatedAt: daysFromNow(-8),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-10) },
    statusHistory: [
      { status: 'submitted', changedBy: U_OTHER, changedByName: 'Carolina Rivas', changedAt: daysFromNow(-12) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-10) },
      { status: 'not_admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-8), notes: 'Investigador sin grado doctoral acreditado.' },
    ],
  },

  // ── admissible (3) ────────────────────────────────────────────────────────
  {
    id: '55555555-5555-4555-8555-000000000013',
    userId: '11111111-1111-4111-8111-000000000006',
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26017',
    status: 'admissible',
    sectionAnswers: richAnswers('Inteligencia Artificial para Diagnóstico Temprano de Alzheimer', 'Ciencias Médicas y de Salud'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-16),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-16),
    createdAt: daysFromNow(-25),
    updatedAt: daysFromNow(-11),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-14) },
    statusHistory: [
      { status: 'submitted', changedBy: '11111111-1111-4111-8111-000000000006', changedByName: 'Jorge Fuentes', changedAt: daysFromNow(-16) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-14) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-11), notes: 'Cumple todos los requisitos.' },
    ],
  },
  {
    id: '55555555-5555-4555-8555-000000000014',
    userId: U_BASIC,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26018',
    status: 'admissible',
    sectionAnswers: richAnswers('Energía Geotérmica en Zonas Volcánicas Andinas', 'Ingeniería y Tecnología'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-15),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-15),
    createdAt: daysFromNow(-23),
    updatedAt: daysFromNow(-10),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-13) },
    statusHistory: [
      { status: 'submitted', changedBy: U_BASIC, changedByName: 'María González', changedAt: daysFromNow(-15) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-13) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-10) },
    ],
  },
  {
    id: '55555555-5555-4555-8555-000000000015',
    userId: U_OTHER,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26019',
    status: 'admissible',
    sectionAnswers: richAnswers('Sistemas Agroforestales y Captura de Carbono', 'Ciencias Agrícolas'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-14),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-14),
    createdAt: daysFromNow(-22),
    updatedAt: daysFromNow(-9),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-12) },
    statusHistory: [
      { status: 'submitted', changedBy: U_OTHER, changedByName: 'Carolina Rivas', changedAt: daysFromNow(-14) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-12) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-9) },
    ],
  },

  // ── under_evaluation (2 more, total 3) ────────────────────────────────────
  {
    id: '55555555-5555-4555-8555-000000000016',
    userId: '11111111-1111-4111-8111-000000000006',
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26020',
    status: 'under_evaluation',
    sectionAnswers: richAnswers('Fusión Nuclear por Confinamiento Inercial', 'Ingeniería y Tecnología', [
      { name: 'Alejandro Pizarro', email: 'a.pizarro@uchile.cl', run: '15.432.100-K', documentType: 'run',
        affiliation: 'Universidad de Chile', doctorateYear: 2012, doctorateMonth: 6, type: 'consolidated', gender: 'M', role: 'COI',
        institutionType: 'Nacional', country: 'Chile' },
      intlCOI('Thomas Müller', 't.mueller@lmu.de', 'Ludwig-Maximilians-Universität München', 'Alemania', 2007, 6, 'M'),
      intlCOI('Wei Zhang', 'w.zhang@caltech.edu', 'California Institute of Technology', 'Estados Unidos', 2008, 6, 'M'),
    ]),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-20),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-20),
    createdAt: daysFromNow(-30),
    updatedAt: daysFromNow(-12),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-18) },
    statusHistory: [
      { status: 'submitted', changedBy: '11111111-1111-4111-8111-000000000006', changedByName: 'Jorge Fuentes', changedAt: daysFromNow(-20) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-18) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-15) },
      { status: 'under_evaluation', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-12), notes: 'Asignada a evaluadores.' },
    ],
  },
  {
    id: '55555555-5555-4555-8555-000000000017',
    userId: U_OTHER,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26021',
    status: 'under_evaluation',
    sectionAnswers: richAnswers('Neuroplasticidad en Pacientes con ACV', 'Ciencias Médicas y de Salud', [
      { name: 'Sofía Vargas', email: 's.vargas@udec.cl', run: '14.876.543-2', documentType: 'run',
        affiliation: 'Universidad de Concepción', doctorateYear: 2016, doctorateMonth: 6, type: 'emerging', gender: 'F', role: 'COI',
        institutionType: 'Nacional', country: 'Chile' },
      intlCOI('François Martin', 'f.martin@inserm.fr', 'Institut National de la Santé et de la Recherche Médicale', 'Francia', 2006, 6, 'M'),
      intlCOI('Alejandro Fernández', 'a.fernandez@ub.edu', 'Universidad de Barcelona', 'España', 2010, 6, 'M'),
    ]),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-19),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-19),
    createdAt: daysFromNow(-28),
    updatedAt: daysFromNow(-11),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-17) },
    statusHistory: [
      { status: 'submitted', changedBy: U_OTHER, changedByName: 'Carolina Rivas', changedAt: daysFromNow(-19) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-17) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-14) },
      { status: 'under_evaluation', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-11) },
    ],
  },

  // ── under_evaluation — coverage: none (all evaluators opted out) ───────────
  {
    id: '55555555-5555-4555-8555-000000000035',
    userId: U_BASIC,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26051',
    status: 'under_evaluation',
    sectionAnswers: richAnswers('Gobernanza en sistemas de educación superior comparada', 'Ciencias Sociales'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-16),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-16),
    createdAt: daysFromNow(-26),
    updatedAt: daysFromNow(-10),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-14) },
    statusHistory: [
      { status: 'submitted', changedBy: U_BASIC, changedByName: 'María González', changedAt: daysFromNow(-16) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-14) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-12) },
      { status: 'under_evaluation', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-10) },
    ],
  },

  // ── under_evaluation — coverage: incomplete (1 active evaluator) ───────────
  {
    id: '55555555-5555-4555-8555-000000000036',
    userId: U_OTHER,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26052',
    status: 'under_evaluation',
    sectionAnswers: richAnswers('Sensores ambientales de bajo costo basados en nanotecnología', 'Ingeniería y Tecnología'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-14),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-14),
    createdAt: daysFromNow(-24),
    updatedAt: daysFromNow(-8),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-12) },
    statusHistory: [
      { status: 'submitted', changedBy: U_OTHER, changedByName: 'Carolina Rivas', changedAt: daysFromNow(-14) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-12) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-10) },
      { status: 'under_evaluation', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-8) },
    ],
  },

  // ── under_evaluation — arbitraje: árbitro invitado (pending) ────────────────
  {
    id: APP_053,
    userId: U_BASIC,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26053',
    status: 'under_evaluation',
    sectionAnswers: richAnswers('Epistemología y modelos de causalidad en ciencias biomédicas', 'Ciencias de la Salud'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-18),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-18),
    createdAt: daysFromNow(-28),
    updatedAt: daysFromNow(-1),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-16) },
    statusHistory: [
      { status: 'submitted', changedBy: U_BASIC, changedByName: 'María González', changedAt: daysFromNow(-18) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-16) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-14) },
      { status: 'under_evaluation', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-12) },
    ],
  },

  // ── under_evaluation — arbitraje: árbitro sin iniciar (accepted_not_started) ─
  {
    id: APP_054,
    userId: U_OTHER,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26054',
    status: 'under_evaluation',
    sectionAnswers: richAnswers('Impacto socioeconómico de tecnologías de energía renovable en zonas rurales', 'Ciencias Sociales'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-15),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-15),
    createdAt: daysFromNow(-25),
    updatedAt: daysFromNow(-1),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-13) },
    statusHistory: [
      { status: 'submitted', changedBy: U_OTHER, changedByName: 'Carolina Rivas', changedAt: daysFromNow(-15) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-13) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-11) },
      { status: 'under_evaluation', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-9) },
    ],
  },

  // ── under_evaluation — requiere árbitro (desviación detectada, sin árbitro asignado) ─
  {
    id: APP_055,
    userId: U_OTHER,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26055',
    status: 'under_evaluation',
    sectionAnswers: richAnswers('Modelos predictivos de riesgo cardiovascular en población andina', 'Ciencias de la Salud'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-20),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-20),
    createdAt: daysFromNow(-30),
    updatedAt: daysFromNow(-5),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-18) },
    statusHistory: [
      { status: 'submitted', changedBy: U_OTHER, changedByName: 'Carolina Rivas', changedAt: daysFromNow(-20) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-18) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-16) },
      { status: 'under_evaluation', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-14) },
    ],
  },

  // ── under_evaluation — arbitraje finalizado ──────────────────────────────────
  {
    id: APP_056,
    userId: U_OTHER,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26056',
    status: 'under_evaluation',
    sectionAnswers: richAnswers('Modelos de predicción de recaída en enfermedades autoinmunes', 'Ciencias de la Salud'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-30),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-30),
    createdAt: daysFromNow(-35),
    updatedAt: daysFromNow(-2),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-28) },
    statusHistory: [
      { status: 'submitted', changedBy: U_OTHER, changedByName: 'Carolina Rivas', changedAt: daysFromNow(-30) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-28) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-26) },
      { status: 'under_evaluation', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-24) },
    ],
  },

  // ── under_evaluation — evaluando (in_progress) ───────────────────────────
  {
    id: APP_057,
    userId: U_BIOL,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26057',
    status: 'under_evaluation',
    sectionAnswers: richAnswers('Mecanismos epigenéticos en la diferenciación celular troncal', 'Ciencias Biológicas'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-8),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-8),
    createdAt: daysFromNow(-14),
    updatedAt: daysFromNow(-3),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-6) },
    statusHistory: [
      { status: 'submitted', changedBy: U_BIOL, changedByName: 'Ana Torres', changedAt: daysFromNow(-8) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-6) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-5) },
      { status: 'under_evaluation', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-4) },
    ],
  },
  {
    id: APP_058,
    userId: U_HIST,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26058',
    status: 'under_evaluation',
    sectionAnswers: richAnswers('Patrones migratorios y redes de sociabilidad en el Cono Sur, 1880–1950', 'Historia, Geografía y Ciencia Política'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-10),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-10),
    createdAt: daysFromNow(-16),
    updatedAt: daysFromNow(-5),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-8) },
    statusHistory: [
      { status: 'submitted', changedBy: U_HIST, changedByName: 'Diego Morales', changedAt: daysFromNow(-10) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-8) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-7) },
      { status: 'under_evaluation', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-6) },
    ],
  },
  {
    id: APP_059,
    userId: U_ECO,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26059',
    status: 'under_evaluation',
    sectionAnswers: richAnswers('Acceso a energía renovable y reducción de pobreza en zonas rurales', 'Economía y Administración'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-6),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-6),
    createdAt: daysFromNow(-10),
    updatedAt: daysFromNow(-2),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-4) },
    statusHistory: [
      { status: 'submitted', changedBy: U_ECO, changedByName: 'Valentina Pérez', changedAt: daysFromNow(-6) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-4) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-3) },
      { status: 'under_evaluation', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-2) },
    ],
  },

  // ── under_evaluation — sin disponibilidad (evaluadores al/sobre límite) ────
  ...((() => {
    const mkApp = (id: string, num: string, userId: string, title: string, faculty: string, daysAgo: number) => ({
      id, userId, formTemplateId: T_OPEN,
      applicationNumber: num, status: 'under_evaluation' as const,
      sectionAnswers: richAnswers(title, faculty),
      sectionStatus: allSectionsCompleted,
      lastSaved: daysFromNow(-daysAgo),
      complianceConfirmed: true,
      submittedAt: daysFromNow(-daysAgo),
      createdAt: daysFromNow(-daysAgo - 6),
      updatedAt: daysFromNow(-daysAgo + 2),
      awardNotifiedAt: null,
      reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-daysAgo + 1) },
      statusHistory: [
        { status: 'submitted' as const,                     changedBy: userId, changedByName: 'Investigador', changedAt: daysFromNow(-daysAgo) },
        { status: 'under_admissibility_review' as const,    changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-daysAgo + 1) },
        { status: 'admissible' as const,                    changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-daysAgo + 2) },
        { status: 'under_evaluation' as const,              changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-daysAgo + 3) },
      ],
    });
    return [
      mkApp(APP_060, 'AV26060', U_QUI,    'Fotocatálisis solar para tratamiento de aguas residuales',     'Química y Farmacia',                    10),
      mkApp(APP_061, 'AV26061', U_BIOL,   'Epidemiología molecular de resistencia antimicrobiana',        'Ciencias Biológicas',                   12),
      mkApp(APP_062, 'AV26062', U_BIOL,   'Redes neuronales para diagnóstico de imágenes médicas',        'Ciencias Biológicas',                    9),
      mkApp(APP_063, 'AV26063', U_CS,     'Impacto socioeconómico de la migración en zonas rurales',      'Ciencias Sociales',                     11),
      mkApp(APP_064, 'AV26064', U_BIOL,   'Genómica funcional de plantas endémicas chilenas',             'Ciencias Biológicas',                    8),
      mkApp(APP_065, 'AV26065', U_QUI,    'Síntesis de nanomateriales para catálisis heterogénea',        'Química y Farmacia',                    13),
      mkApp(APP_066, 'AV26066', U_HIST,   'Arqueología del paisaje en el norte árido de Chile',           'Historia, Geografía y Ciencia Política',  7),
      mkApp(APP_067, 'AV26067', U_QUI,    'Bioquímica de compuestos bioactivos de macroalgas',            'Química y Farmacia',                    14),
      mkApp(APP_068, 'AV26068', U_BIOL,   'Robótica colaborativa en entornos hospitalarios',              'Ciencias Biológicas',                    6),
      mkApp(APP_069, 'AV26069', U_CS,     'Análisis de redes sociales y polarización política',           'Ciencias Sociales',                     10),
      mkApp(APP_06A, 'AV26070', U_ECO,    'Modelos de aprendizaje automático en finanzas conductuales',   'Economía y Administración',              9),
      mkApp(APP_06B, 'AV26071', U_BIOL,   'Modelamiento climático en ecosistemas andinos',                'Ciencias Biológicas',                   11),
    ];
  })()),

  // ── evaluated (3) ─────────────────────────────────────────────────────────
  {
    id: '55555555-5555-4555-8555-000000000018',
    userId: '11111111-1111-4111-8111-000000000006',
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26022',
    status: 'evaluated',
    sectionAnswers: richAnswers('Computación Cuántica para Simulación Molecular', 'Ingeniería y Tecnología', [
      intlCOI('Priya Sharma', 'p.sharma@stanford.edu', 'Stanford University', 'Estados Unidos', 2007, 6, 'F'),
      intlCOI('Hans Gruber', 'h.gruber@ethz.ch', 'ETH Zürich', 'Suiza', 2004, 6, 'M'),
      intlCOI('James Wilson', 'j.wilson@mit.edu', 'Massachusetts Institute of Technology', 'Estados Unidos', 2005, 6, 'M'),
      { name: 'Valentina Morales', email: 'v.morales@usach.cl', run: '', documentType: 'rut', affiliation: 'Universidad de Santiago de Chile', doctorateYear: 2014, doctorateMonth: 6, type: 'consolidated', gender: 'F', role: 'COI', institutionType: 'Nacional', country: 'Chile' },
    ]),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-25),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-25),
    createdAt: daysFromNow(-35),
    updatedAt: daysFromNow(-8),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-23) },
    statusHistory: [
      { status: 'submitted', changedBy: '11111111-1111-4111-8111-000000000006', changedByName: 'Jorge Fuentes', changedAt: daysFromNow(-25) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-23) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-20) },
      { status: 'under_evaluation', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-15) },
      { status: 'evaluated', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-8) },
    ],
  },
  {
    id: '55555555-5555-4555-8555-000000000019',
    userId: U_BASIC,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26023',
    status: 'evaluated',
    sectionAnswers: richAnswers('Vacunas de ARNm contra Enfermedades Tropicales', 'Ciencias Médicas y de Salud', [
      intlCOI('Carmen López', 'c.lopez@uam.es', 'Universidad Autónoma de Madrid', 'España', 2009, 6, 'F'),
      intlCOI('Ana Souza', 'a.souza@usp.br', 'Universidade de São Paulo', 'Brasil', 2012, 6, 'F'),
      intlCOI('Roberto Conti', 'r.conti@uniroma1.it', 'Sapienza Università di Roma', 'Italia', 2009, 6, 'M'),
    ]),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-24),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-24),
    createdAt: daysFromNow(-33),
    updatedAt: daysFromNow(-7),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-22) },
    statusHistory: [
      { status: 'submitted', changedBy: U_BASIC, changedByName: 'María González', changedAt: daysFromNow(-24) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-22) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-19) },
      { status: 'under_evaluation', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-14) },
      { status: 'evaluated', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-7) },
    ],
  },
  {
    id: '55555555-5555-4555-8555-00000000001A',
    userId: U_OTHER,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26024',
    status: 'evaluated',
    sectionAnswers: richAnswers('Impacto del Cambio Climático en la Pesca Artesanal', 'Ciencias Agrícolas', [
      intlCOI('Martín Olivares', 'm.olivares@uba.ar', 'Universidad de Buenos Aires', 'Argentina', 2011, 6, 'M'),
      intlCOI('Alejandro Fernández', 'a.fernandez@ub.edu', 'Universidad de Barcelona', 'España', 2010, 6, 'M'),
      { name: 'Felipe Castillo', email: 'f.castillo@pucv.cl', run: '', documentType: 'rut', affiliation: 'Pontificia Universidad Católica de Valparaíso', doctorateYear: 2013, doctorateMonth: 6, type: 'emerging', gender: 'M', role: 'COI', institutionType: 'Nacional', country: 'Chile' },
    ]),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-23),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-23),
    createdAt: daysFromNow(-32),
    updatedAt: daysFromNow(-6),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-21) },
    statusHistory: [
      { status: 'submitted', changedBy: U_OTHER, changedByName: 'Carolina Rivas', changedAt: daysFromNow(-23) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-21) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-18) },
      { status: 'under_evaluation', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-13) },
      { status: 'evaluated', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-6) },
    ],
  },

  // ── selected (2 more, total 3) ─────────────────────────────────────────────
  {
    id: '55555555-5555-4555-8555-00000000001B',
    userId: '11111111-1111-4111-8111-000000000006',
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26025',
    status: 'evaluated',
    sectionAnswers: richAnswers('Paneles Solares de Alta Eficiencia para Zonas Rurales', 'Ingeniería y Tecnología'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-28),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-28),
    createdAt: daysFromNow(-38),
    updatedAt: daysFromNow(-8),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-26) },
    statusHistory: [
      { status: 'submitted', changedBy: '11111111-1111-4111-8111-000000000006', changedByName: 'Jorge Fuentes', changedAt: daysFromNow(-28) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-26) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-22) },
      { status: 'under_evaluation', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-16) },
      { status: 'evaluated', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-8) },
    ],
  },
  {
    id: '55555555-5555-4555-8555-00000000001C',
    userId: U_OTHER,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26026',
    status: 'evaluated',
    sectionAnswers: richAnswers('Terapia Génica para Distrofia Muscular', 'Ciencias Médicas y de Salud'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-27),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-27),
    createdAt: daysFromNow(-37),
    updatedAt: daysFromNow(-7),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-25) },
    statusHistory: [
      { status: 'submitted', changedBy: U_OTHER, changedByName: 'Carolina Rivas', changedAt: daysFromNow(-27) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-25) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-21) },
      { status: 'under_evaluation', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-15) },
      { status: 'evaluated', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-7) },
    ],
  },

  // ── evaluated (pending fallo) ─────────────────────────────────────────────
  {
    id: '55555555-5555-4555-8555-00000000001D',
    userId: '11111111-1111-4111-8111-000000000006',
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26027',
    status: 'evaluated',
    sectionAnswers: richAnswers('Plataforma Digital para Educación Rural Inclusiva', 'Ciencias Sociales'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-29),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-29),
    createdAt: daysFromNow(-39),
    updatedAt: daysFromNow(-9),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-27) },
    statusHistory: [
      { status: 'submitted', changedBy: '11111111-1111-4111-8111-000000000006', changedByName: 'Jorge Fuentes', changedAt: daysFromNow(-29) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-27) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-23) },
      { status: 'under_evaluation', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-17) },
      { status: 'evaluated', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-9) },
    ],
  },
  {
    id: '55555555-5555-4555-8555-00000000001E',
    userId: U_BASIC,
    formTemplateId: T_OPEN,
    applicationNumber: 'AV26028',
    status: 'evaluated',
    sectionAnswers: richAnswers('Criptomonedas y Economía Informal en Chile', 'Ciencias Sociales'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-26),
    complianceConfirmed: true,
    submittedAt: daysFromNow(-26),
    createdAt: daysFromNow(-36),
    updatedAt: daysFromNow(-8),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-24) },
    statusHistory: [
      { status: 'submitted', changedBy: U_BASIC, changedByName: 'María González', changedAt: daysFromNow(-26) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-24) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-20) },
      { status: 'under_evaluation', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-14) },
      { status: 'evaluated', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-8) },
    ],
  },

  // ---------------------------------------------------------------------------
  // Ana Torres — Facultad de Ciencias Biológicas (5 apps)
  // ---------------------------------------------------------------------------
  {
    id: '55555555-5555-4555-8555-00000000001F',
    userId: U_BIOL, formTemplateId: T_OPEN,
    applicationNumber: 'AV26029', status: 'submitted',
    sectionAnswers: richAnswers('Diversidad microbiana en ecosistemas de alta montaña', 'Ciencias Naturales'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-10), complianceConfirmed: true,
    submittedAt: daysFromNow(-10), createdAt: daysFromNow(-20), updatedAt: daysFromNow(-10),
    awardNotifiedAt: null,
    statusHistory: [{ status: 'submitted', changedBy: U_BIOL, changedByName: 'Ana Torres', changedAt: daysFromNow(-10) }],
  },
  {
    id: '55555555-5555-4555-8555-000000000020',
    userId: U_BIOL, formTemplateId: T_OPEN,
    applicationNumber: 'AV26030', status: 'submitted',
    sectionAnswers: richAnswers('Mecanismos epigenéticos en respuesta al estrés hídrico', 'Ciencias Naturales'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-15), complianceConfirmed: true,
    submittedAt: daysFromNow(-15), createdAt: daysFromNow(-25), updatedAt: daysFromNow(-15),
    awardNotifiedAt: null,
    statusHistory: [{ status: 'submitted', changedBy: U_BIOL, changedByName: 'Ana Torres', changedAt: daysFromNow(-15) }],
  },
  {
    id: '55555555-5555-4555-8555-000000000021',
    userId: U_BIOL, formTemplateId: T_OPEN,
    applicationNumber: 'AV26031', status: 'admissible',
    sectionAnswers: richAnswers('Interacción huésped-patógeno en Drosophila melanogaster', 'Ciencias Naturales'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-30), complianceConfirmed: true,
    submittedAt: daysFromNow(-30), createdAt: daysFromNow(-40), updatedAt: daysFromNow(-20),
    awardNotifiedAt: null,
    reviewerAssignment: { adminId: ADMIN_DINV, adminName: 'Admin DINV', assignedAt: daysFromNow(-28) },
    statusHistory: [
      { status: 'submitted', changedBy: U_BIOL, changedByName: 'Ana Torres', changedAt: daysFromNow(-30) },
      { status: 'under_admissibility_review', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-28) },
      { status: 'admissible', changedBy: ADMIN_DINV, changedByName: 'Admin DINV', changedAt: daysFromNow(-20) },
    ],
  },
  {
    id: '55555555-5555-4555-8555-000000000022',
    userId: U_BIOL, formTemplateId: T_OPEN,
    applicationNumber: 'AV26032', status: 'draft',
    sectionAnswers: richAnswers('Biomarcadores de inflamación crónica en tejido adiposo', 'Ciencias Médicas'),
    sectionStatus: { APPLICATION_INFORMATION: 'completed', PROJECT_INFORMATION: 'in_progress' },
    lastSaved: daysFromNow(-5), complianceConfirmed: false,
    submittedAt: null, createdAt: daysFromNow(-10), updatedAt: daysFromNow(-5),
    awardNotifiedAt: null, statusHistory: [],
  },
  {
    id: '55555555-5555-4555-8555-000000000023',
    userId: U_BIOL, formTemplateId: T_OPEN,
    applicationNumber: 'AV26033', status: 'draft',
    sectionAnswers: richAnswers('Caracterización del microbioma intestinal en pacientes oncológicos', 'Ciencias Médicas'),
    sectionStatus: { APPLICATION_INFORMATION: 'completed' },
    lastSaved: daysFromNow(-2), complianceConfirmed: false,
    submittedAt: null, createdAt: daysFromNow(-7), updatedAt: daysFromNow(-2),
    awardNotifiedAt: null, statusHistory: [],
  },

  // ---------------------------------------------------------------------------
  // Diego Morales — Facultad de Historia, Geografía y Ciencia Política (4 apps)
  // ---------------------------------------------------------------------------
  {
    id: '55555555-5555-4555-8555-000000000024',
    userId: U_HIST, formTemplateId: T_OPEN,
    applicationNumber: 'AV26034', status: 'submitted',
    sectionAnswers: richAnswers('Cartografía del conflicto territorial mapuche en el siglo XIX', 'Ciencias Sociales'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-12), complianceConfirmed: true,
    submittedAt: daysFromNow(-12), createdAt: daysFromNow(-22), updatedAt: daysFromNow(-12),
    awardNotifiedAt: null,
    statusHistory: [{ status: 'submitted', changedBy: U_HIST, changedByName: 'Diego Morales', changedAt: daysFromNow(-12) }],
  },
  {
    id: '55555555-5555-4555-8555-000000000025',
    userId: U_HIST, formTemplateId: T_OPEN,
    applicationNumber: 'AV26035', status: 'submitted',
    sectionAnswers: richAnswers('Análisis geopolítico de cuencas hídricas compartidas en el Cono Sur', 'Ciencias Sociales'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-18), complianceConfirmed: true,
    submittedAt: daysFromNow(-18), createdAt: daysFromNow(-28), updatedAt: daysFromNow(-18),
    awardNotifiedAt: null,
    statusHistory: [{ status: 'submitted', changedBy: U_HIST, changedByName: 'Diego Morales', changedAt: daysFromNow(-18) }],
  },
  {
    id: '55555555-5555-4555-8555-000000000026',
    userId: U_HIST, formTemplateId: T_OPEN,
    applicationNumber: 'AV26036', status: 'draft',
    sectionAnswers: richAnswers('Participación política juvenil en regímenes de transición democrática', 'Ciencias Sociales'),
    sectionStatus: { APPLICATION_INFORMATION: 'completed', PROJECT_INFORMATION: 'in_progress' },
    lastSaved: daysFromNow(-4), complianceConfirmed: false,
    submittedAt: null, createdAt: daysFromNow(-9), updatedAt: daysFromNow(-4),
    awardNotifiedAt: null, statusHistory: [],
  },
  {
    id: '55555555-5555-4555-8555-000000000027',
    userId: U_HIST, formTemplateId: T_OPEN,
    applicationNumber: 'AV26037', status: 'draft',
    sectionAnswers: richAnswers('Migraciones andinas y construcción de identidad regional', 'Ciencias Sociales'),
    sectionStatus: { APPLICATION_INFORMATION: 'in_progress' },
    lastSaved: daysFromNow(-1), complianceConfirmed: false,
    submittedAt: null, createdAt: daysFromNow(-5), updatedAt: daysFromNow(-1),
    awardNotifiedAt: null, statusHistory: [],
  },

  // ---------------------------------------------------------------------------
  // Valentina Pérez — Facultad de Economía y Administración (3 apps)
  // ---------------------------------------------------------------------------
  {
    id: '55555555-5555-4555-8555-000000000028',
    userId: U_ECO, formTemplateId: T_OPEN,
    applicationNumber: 'AV26038', status: 'submitted',
    sectionAnswers: richAnswers('Impacto de la digitalización en la productividad de las PYME chilenas', 'Ciencias Sociales'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-8), complianceConfirmed: true,
    submittedAt: daysFromNow(-8), createdAt: daysFromNow(-18), updatedAt: daysFromNow(-8),
    awardNotifiedAt: null,
    statusHistory: [{ status: 'submitted', changedBy: U_ECO, changedByName: 'Valentina Pérez', changedAt: daysFromNow(-8) }],
  },
  {
    id: '55555555-5555-4555-8555-000000000029',
    userId: U_ECO, formTemplateId: T_OPEN,
    applicationNumber: 'AV26039', status: 'draft',
    sectionAnswers: richAnswers('Mercados financieros emergentes y volatilidad cambiaria en Latinoamérica', 'Ciencias Sociales'),
    sectionStatus: { APPLICATION_INFORMATION: 'completed', PROJECT_INFORMATION: 'completed' },
    lastSaved: daysFromNow(-3), complianceConfirmed: false,
    submittedAt: null, createdAt: daysFromNow(-10), updatedAt: daysFromNow(-3),
    awardNotifiedAt: null, statusHistory: [],
  },
  {
    id: '55555555-5555-4555-8555-00000000002A',
    userId: U_ECO, formTemplateId: T_OPEN,
    applicationNumber: 'AV26040', status: 'draft',
    sectionAnswers: richAnswers('Modelo de predicción de riesgo crediticio para economías emergentes', 'Ciencias Sociales'),
    sectionStatus: { APPLICATION_INFORMATION: 'in_progress' },
    lastSaved: daysFromNow(-1), complianceConfirmed: false,
    submittedAt: null, createdAt: daysFromNow(-6), updatedAt: daysFromNow(-1),
    awardNotifiedAt: null, statusHistory: [],
  },

  // ---------------------------------------------------------------------------
  // Sebastián Araya — Facultad de Química y Farmacia (3 apps)
  // ---------------------------------------------------------------------------
  {
    id: '55555555-5555-4555-8555-00000000002B',
    userId: U_QUI, formTemplateId: T_OPEN,
    applicationNumber: 'AV26041', status: 'submitted',
    sectionAnswers: richAnswers('Síntesis de compuestos antifúngicos derivados de terpenoides naturales', 'Ciencias Naturales'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-11), complianceConfirmed: true,
    submittedAt: daysFromNow(-11), createdAt: daysFromNow(-21), updatedAt: daysFromNow(-11),
    awardNotifiedAt: null,
    statusHistory: [{ status: 'submitted', changedBy: U_QUI, changedByName: 'Sebastián Araya', changedAt: daysFromNow(-11) }],
  },
  {
    id: '55555555-5555-4555-8555-00000000002C',
    userId: U_QUI, formTemplateId: T_OPEN,
    applicationNumber: 'AV26042', status: 'submitted',
    sectionAnswers: richAnswers('Formulaciones farmacéuticas de liberación controlada para antiparasitarios', 'Ciencias Médicas'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-20), complianceConfirmed: true,
    submittedAt: daysFromNow(-20), createdAt: daysFromNow(-30), updatedAt: daysFromNow(-20),
    awardNotifiedAt: null,
    statusHistory: [{ status: 'submitted', changedBy: U_QUI, changedByName: 'Sebastián Araya', changedAt: daysFromNow(-20) }],
  },
  {
    id: '55555555-5555-4555-8555-00000000002D',
    userId: U_QUI, formTemplateId: T_OPEN,
    applicationNumber: 'AV26043', status: 'draft',
    sectionAnswers: richAnswers('Nanopartículas lipídicas como vectores de ARN terapéutico', 'Ciencias Médicas'),
    sectionStatus: { APPLICATION_INFORMATION: 'completed', PROJECT_INFORMATION: 'in_progress' },
    lastSaved: daysFromNow(-2), complianceConfirmed: false,
    submittedAt: null, createdAt: daysFromNow(-8), updatedAt: daysFromNow(-2),
    awardNotifiedAt: null, statusHistory: [],
  },

  // ---------------------------------------------------------------------------
  // Claudia Vásquez — Facultad de Ciencias Sociales (2 apps)
  // ---------------------------------------------------------------------------
  {
    id: '55555555-5555-4555-8555-00000000002E',
    userId: U_CS, formTemplateId: T_OPEN,
    applicationNumber: 'AV26044', status: 'submitted',
    sectionAnswers: richAnswers('Bienestar subjetivo y exclusión social en comunidades periurbanas', 'Ciencias Sociales'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-9), complianceConfirmed: true,
    submittedAt: daysFromNow(-9), createdAt: daysFromNow(-19), updatedAt: daysFromNow(-9),
    awardNotifiedAt: null,
    statusHistory: [{ status: 'submitted', changedBy: U_CS, changedByName: 'Claudia Vásquez', changedAt: daysFromNow(-9) }],
  },
  {
    id: '55555555-5555-4555-8555-00000000002F',
    userId: U_CS, formTemplateId: T_OPEN,
    applicationNumber: 'AV26045', status: 'draft',
    sectionAnswers: richAnswers('Dinámicas de violencia simbólica en entornos educativos secundarios', 'Ciencias Sociales'),
    sectionStatus: { APPLICATION_INFORMATION: 'in_progress' },
    lastSaved: daysFromNow(-3), complianceConfirmed: false,
    submittedAt: null, createdAt: daysFromNow(-7), updatedAt: daysFromNow(-3),
    awardNotifiedAt: null, statusHistory: [],
  },

  // ---------------------------------------------------------------------------
  // Rodrigo Ibáñez — Instituto de Estética (facultadUC: null — unidad interdisciplinaria)
  // ---------------------------------------------------------------------------
  {
    id: '55555555-5555-4555-8555-000000000030',
    userId: U_ESTETICA, formTemplateId: T_OPEN,
    applicationNumber: 'AV26046', status: 'submitted',
    sectionAnswers: richAnswers('Experiencia estética y cognición encarnada en arte contemporáneo', 'Humanidades'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-13), complianceConfirmed: true,
    submittedAt: daysFromNow(-13), createdAt: daysFromNow(-23), updatedAt: daysFromNow(-13),
    awardNotifiedAt: null,
    statusHistory: [{ status: 'submitted', changedBy: U_ESTETICA, changedByName: 'Rodrigo Ibáñez', changedAt: daysFromNow(-13) }],
  },
  {
    id: '55555555-5555-4555-8555-000000000031',
    userId: U_ESTETICA, formTemplateId: T_OPEN,
    applicationNumber: 'AV26047', status: 'submitted',
    sectionAnswers: richAnswers('Semiótica visual y patrimonio cultural en ciudades latinoamericanas', 'Humanidades'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-22), complianceConfirmed: true,
    submittedAt: daysFromNow(-22), createdAt: daysFromNow(-32), updatedAt: daysFromNow(-22),
    awardNotifiedAt: null,
    statusHistory: [{ status: 'submitted', changedBy: U_ESTETICA, changedByName: 'Rodrigo Ibáñez', changedAt: daysFromNow(-22) }],
  },
  {
    id: '55555555-5555-4555-8555-000000000032',
    userId: U_ESTETICA, formTemplateId: T_OPEN,
    applicationNumber: 'AV26048', status: 'draft',
    sectionAnswers: richAnswers('Filosofía de la imagen y prácticas curatoriales digitales', 'Humanidades'),
    sectionStatus: { APPLICATION_INFORMATION: 'in_progress' },
    lastSaved: daysFromNow(-2), complianceConfirmed: false,
    submittedAt: null, createdAt: daysFromNow(-6), updatedAt: daysFromNow(-2),
    awardNotifiedAt: null, statusHistory: [],
  },

  // ---------------------------------------------------------------------------
  // Paula Contreras — College UC (facultadUC: null — unidad independiente)
  // ---------------------------------------------------------------------------
  {
    id: '55555555-5555-4555-8555-000000000033',
    userId: U_COLLEGE, formTemplateId: T_OPEN,
    applicationNumber: 'AV26049', status: 'submitted',
    sectionAnswers: richAnswers('Educación general y pensamiento crítico en licenciaturas multidisciplinarias', 'Ciencias Sociales'),
    sectionStatus: allSectionsCompleted,
    lastSaved: daysFromNow(-7), complianceConfirmed: true,
    submittedAt: daysFromNow(-7), createdAt: daysFromNow(-17), updatedAt: daysFromNow(-7),
    awardNotifiedAt: null,
    statusHistory: [{ status: 'submitted', changedBy: U_COLLEGE, changedByName: 'Paula Contreras', changedAt: daysFromNow(-7) }],
  },
  {
    id: '55555555-5555-4555-8555-000000000034',
    userId: U_COLLEGE, formTemplateId: T_OPEN,
    applicationNumber: 'AV26050', status: 'draft',
    sectionAnswers: richAnswers('Aprendizaje integrado y currículos transversales en educación superior', 'Ciencias Sociales'),
    sectionStatus: { APPLICATION_INFORMATION: 'completed', PROJECT_INFORMATION: 'in_progress' },
    lastSaved: daysFromNow(-1), complianceConfirmed: false,
    submittedAt: null, createdAt: daysFromNow(-8), updatedAt: daysFromNow(-1),
    awardNotifiedAt: null, statusHistory: [],
  },
];

// ---------------------------------------------------------------------------
// Evaluator assignments + evaluations (on the under-review application)
// ---------------------------------------------------------------------------

const EV_A = '22222222-2222-4222-8222-00000000000A';
const EV_B = '22222222-2222-4222-8222-00000000000B';
const EV_C = '22222222-2222-4222-8222-00000000000C';
const EV_D = '22222222-2222-4222-8222-00000000000D';
const EV_E = '22222222-2222-4222-8222-00000000000E';
const EV_F = '22222222-2222-4222-8222-00000000000F';
const EV_G = '22222222-2222-4222-8222-000000000010';
const EV_H = '22222222-2222-4222-8222-000000000011'; // María Eugenia Contreras

const ASG_A = '66666666-6666-4666-8666-000000000001';
const ASG_B = '66666666-6666-4666-8666-000000000002';
const ASG_C = '66666666-6666-4666-8666-000000000003';
const ASG_D = '66666666-6666-4666-8666-000000000004';
const ASG_E = '66666666-6666-4666-8666-000000000005';
const ASG_007_A = '66666666-6666-4666-8666-000000000006';
const ASG_007_B = '66666666-6666-4666-8666-000000000007';
const ASG_008_A = '66666666-6666-4666-8666-000000000008';
const ASG_008_B = '66666666-6666-4666-8666-000000000009';
const ASG_018_A = '66666666-6666-4666-8666-000000000010';
const ASG_018_B = '66666666-6666-4666-8666-000000000011';
const ASG_019_A = '66666666-6666-4666-8666-000000000012';
const ASG_019_B = '66666666-6666-4666-8666-000000000013';
const ASG_01A_A = '66666666-6666-4666-8666-000000000014';
const ASG_01A_B = '66666666-6666-4666-8666-000000000015';
// Overdue assignments for REP-012 (APP-2026-0020 and APP-2026-0021)
const ASG_020_A = '66666666-6666-4666-8666-000000000016';
const ASG_020_B = '66666666-6666-4666-8666-000000000017';
const ASG_021_A = '66666666-6666-4666-8666-000000000018';
const ASG_021_B = '66666666-6666-4666-8666-000000000019';
// Declined assignment for REP-011 (EV_H declined APP-2026-0020)
const ASG_020_C = '66666666-6666-4666-8666-00000000001A';
// Assignments for AV26051 (coverage: none — all opted out)
const ASG_051_A = '66666666-6666-4666-8666-00000000001B';
const ASG_051_B = '66666666-6666-4666-8666-00000000001C';
// Assignments for AV26052 (coverage: incomplete — 1 active + 1 declined)
const ASG_052_A = '66666666-6666-4666-8666-00000000001D';
const ASG_052_B = '66666666-6666-4666-8666-00000000001E';
// Arbitration intermediate state scenarios
const APP_053 = '55555555-5555-4555-8555-000000000053'; // árbitro invitado (pending)
const APP_054 = '55555555-5555-4555-8555-000000000054'; // árbitro sin iniciar (accepted_not_started)
const APP_055 = '55555555-5555-4555-8555-000000000055'; // requiere árbitro (desviación, sin árbitro aún)
const ASG_055_A = '66666666-6666-4666-8666-000000000025';
const ASG_055_B = '66666666-6666-4666-8666-000000000026';
const ASG_053_A = '66666666-6666-4666-8666-00000000001F';
const ASG_053_B = '66666666-6666-4666-8666-000000000020';
const ASG_053_C = '66666666-6666-4666-8666-000000000021'; // arbiter: EV_F pending
const ASG_054_A = '66666666-6666-4666-8666-000000000022';
const ASG_054_B = '66666666-6666-4666-8666-000000000023';
const ASG_054_C = '66666666-6666-4666-8666-000000000024'; // arbiter: EV_H accepted_not_started
const APP_056 = '55555555-5555-4555-8555-000000000056'; // arbitraje finalizado
const ASG_056_A = '66666666-6666-4666-8666-000000000027';
const ASG_056_B = '66666666-6666-4666-8666-000000000028';
const ASG_056_C = '66666666-6666-4666-8666-000000000029'; // arbiter: EV_D completed
// Evaluando — progress: in_progress (3 apps)
const APP_057 = '55555555-5555-4555-8555-000000000057';
const APP_058 = '55555555-5555-4555-8555-000000000058';
const APP_059 = '55555555-5555-4555-8555-000000000059';
const ASG_057_A = '66666666-6666-4666-8666-00000000002A';
const ASG_057_B = '66666666-6666-4666-8666-00000000002B';
const ASG_058_A = '66666666-6666-4666-8666-00000000002C';
const ASG_058_B = '66666666-6666-4666-8666-00000000002D';
const ASG_059_A = '66666666-6666-4666-8666-00000000002E';
const ASG_059_B = '66666666-6666-4666-8666-00000000002F';
const ASG_B_020 = '66666666-6666-4666-8666-000000000030'; // EV_B pending — AV26020
const ASG_B_021 = '66666666-6666-4666-8666-000000000031'; // EV_B pending — AV26021
const ASG_B_052 = '66666666-6666-4666-8666-000000000033'; // EV_B declined — AV26052
const ASG_B_055 = '66666666-6666-4666-8666-000000000034'; // EV_B conflict_declared — AV26055
const ASG_B_051 = '66666666-6666-4666-8666-000000000032'; // EV_B accepted_not_started — AV26051
// ── Sin disponibilidad — 3 evaluators at/over capacity ───────────────────────
const EV_I = '22222222-2222-4222-8222-000000000012'; // Pedro Alarcón  — disponible = 0
const EV_J = '22222222-2222-4222-8222-000000000013'; // Sofía Vidal    — disponible = -2
const EV_K = '22222222-2222-4222-8222-000000000014'; // Matías Rojas   — disponible = -1
const APP_060 = '55555555-5555-4555-8555-000000000060';
const APP_061 = '55555555-5555-4555-8555-000000000061';
const APP_062 = '55555555-5555-4555-8555-000000000062';
const APP_063 = '55555555-5555-4555-8555-000000000063';
const APP_064 = '55555555-5555-4555-8555-000000000064';
const APP_065 = '55555555-5555-4555-8555-000000000065';
const APP_066 = '55555555-5555-4555-8555-000000000066';
const APP_067 = '55555555-5555-4555-8555-000000000067';
const APP_068 = '55555555-5555-4555-8555-000000000068';
const APP_069 = '55555555-5555-4555-8555-000000000069';
const APP_06A = '55555555-5555-4555-8555-00000000006A';
const APP_06B = '55555555-5555-4555-8555-00000000006B';

/**
 * Every hand-written assignment below predates the invitation-tracking field, and
 * all of them represent evaluators who were already notified — that is what the
 * demo scenarios assume. Rather than repeat `invitationSentAt` in ~80 literals,
 * `buildAssignments` backfills it from `assignedAt` for any record that omits it.
 * Only assignments created after this change (by the bulk wizard with the switch
 * off, or by "Asignar" in the manage-evaluators modal) start out un-invited.
 */
const buildAssignments = (): StoredAssignment[] =>
  rawAssignments().map((a) => ({
    ...a,
    invitationSentAt: a.invitationSentAt ?? a.assignedAt,
  }));

const rawAssignments = (): StoredAssignment[] => [
  {
    id: ASG_A,
    applicationId: APP_UNDER_REVIEW,
    evaluatorId: EV_A,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-8),
    status: 'completed',
    evaluationSubmittedAt: daysFromNow(-2),
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-7),
  },
  {
    id: ASG_B,
    applicationId: APP_UNDER_REVIEW,
    evaluatorId: EV_B,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-2),
    status: 'in_progress',
    evaluationSubmittedAt: null,
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-1),
  },
  // EV_B additional assignments — pending invitations and accepted-not-started
  {
    id: ASG_B_020,
    applicationId: '55555555-5555-4555-8555-000000000016',
    evaluatorId: EV_B,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-3),
    status: 'pending',
    evaluationSubmittedAt: null,
    conflictDeclared: false,
    conflictStatus: null,
    conflictDetails: null,
    conflictDeclaredAt: null,
  },
  {
    id: ASG_B_021,
    applicationId: '55555555-5555-4555-8555-000000000017',
    evaluatorId: EV_B,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-1),
    status: 'pending',
    evaluationSubmittedAt: null,
    conflictDeclared: false,
    conflictStatus: null,
    conflictDetails: null,
    conflictDeclaredAt: null,
  },
  {
    id: ASG_B_051,
    applicationId: '55555555-5555-4555-8555-000000000035',
    evaluatorId: EV_B,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-5),
    status: 'accepted_not_started',
    evaluationSubmittedAt: null,
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: null,
  },
  {
    id: ASG_C,
    applicationId: APP_UNDER_REVIEW,
    evaluatorId: EV_C,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-3),
    status: 'conflict_declared',
    evaluationSubmittedAt: null,
    conflictDeclared: true,
    conflictStatus: 'conflict_declared',
    conflictDetails: 'Co-autoría reciente con el postulante.',
    conflictDeclaredAt: daysFromNow(-2),
  },
  {
    id: ASG_D,
    applicationId: APP_UNDER_REVIEW,
    evaluatorId: EV_D,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-8),
    status: 'completed',
    evaluationSubmittedAt: daysFromNow(-3),
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-7),
  },
  {
    id: ASG_E,
    applicationId: APP_UNDER_REVIEW,
    evaluatorId: EV_E,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-6),
    status: 'declined',
    evaluationSubmittedAt: null,
    conflictDeclared: false,
    conflictStatus: null,
    conflictDetails: null,
    conflictDeclaredAt: null,
    declineReason: 'thematic',
    declineNote: 'El proyecto se centra en políticas públicas de salud, área alejada de mi línea de investigación en economía experimental.',
  },
  // Assignments for APP-2025-0004 (Biomateriales — selected)
  {
    id: ASG_007_A,
    applicationId: '55555555-5555-4555-8555-000000000007',
    evaluatorId: EV_A,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-405),
    status: 'completed',
    evaluationSubmittedAt: daysFromNow(-385),
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-404),
  },
  {
    id: ASG_007_B,
    applicationId: '55555555-5555-4555-8555-000000000007',
    evaluatorId: EV_B,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-405),
    status: 'completed',
    evaluationSubmittedAt: daysFromNow(-382),
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-404),
  },
  // Assignments for APP-2026-0022 (Computación Cuántica — evaluated)
  {
    id: ASG_018_A,
    applicationId: '55555555-5555-4555-8555-000000000018',
    evaluatorId: EV_A,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-15),
    status: 'completed',
    evaluationSubmittedAt: daysFromNow(-9),
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-14),
  },
  {
    id: ASG_018_B,
    applicationId: '55555555-5555-4555-8555-000000000018',
    evaluatorId: EV_B,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-15),
    status: 'completed',
    evaluationSubmittedAt: daysFromNow(-8),
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-14),
  },
  // Assignments for APP-2026-0023 (Vacunas ARNm — evaluated)
  {
    id: ASG_019_A,
    applicationId: '55555555-5555-4555-8555-000000000019',
    evaluatorId: EV_C,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-14),
    status: 'completed',
    evaluationSubmittedAt: daysFromNow(-8),
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-13),
  },
  {
    id: ASG_019_B,
    applicationId: '55555555-5555-4555-8555-000000000019',
    evaluatorId: EV_D,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-14),
    status: 'completed',
    evaluationSubmittedAt: daysFromNow(-7),
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-13),
  },
  // Assignments for APP-2026-0024 (Pesca Artesanal — evaluated)
  {
    id: ASG_01A_A,
    applicationId: '55555555-5555-4555-8555-00000000001A',
    evaluatorId: EV_E,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-13),
    status: 'completed',
    evaluationSubmittedAt: daysFromNow(-7),
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-12),
  },
  {
    id: ASG_01A_B,
    applicationId: '55555555-5555-4555-8555-00000000001A',
    evaluatorId: EV_A,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-13),
    status: 'completed',
    evaluationSubmittedAt: daysFromNow(-6),
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-12),
  },
  // Overdue assignments for APP-2026-0020 (Fusión Nuclear — under_evaluation, 12+ days)
  {
    id: ASG_020_A,
    applicationId: '55555555-5555-4555-8555-000000000016',
    evaluatorId: EV_F,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-12),
    status: 'in_progress',
    evaluationSubmittedAt: null,
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: null,
  },
  {
    id: ASG_020_B,
    applicationId: '55555555-5555-4555-8555-000000000016',
    evaluatorId: EV_G,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-12),
    status: 'in_progress',
    evaluationSubmittedAt: null,
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: null,
  },
  // Overdue assignments for APP-2026-0021 (Neuroplasticidad — under_evaluation, 11 days)
  {
    id: ASG_021_A,
    applicationId: '55555555-5555-4555-8555-000000000017',
    evaluatorId: EV_G,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-11),
    status: 'in_progress',
    evaluationSubmittedAt: null,
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: null,
  },
  {
    id: ASG_021_B,
    applicationId: '55555555-5555-4555-8555-000000000017',
    evaluatorId: EV_H,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-11),
    status: 'pending',
    evaluationSubmittedAt: null,
    conflictDeclared: false,
    conflictStatus: null,
    conflictDetails: null,
    conflictDeclaredAt: null,
  },
  // Declined invitation for APP-2026-0020 (EV_H — REP-011 declined scenario)
  {
    id: ASG_020_C,
    applicationId: '55555555-5555-4555-8555-000000000016',
    evaluatorId: EV_H,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-12),
    status: 'conflict_declared',
    evaluationSubmittedAt: null,
    conflictDeclared: true,
    conflictStatus: 'conflict_declared',
    conflictDetails: 'Conozco personalmente a uno de los investigadores del equipo.',
    conflictDeclaredAt: daysFromNow(-11),
  },
  // Assignments for AV26051 (Gobernanza educación — coverage: none)
  {
    id: ASG_051_A,
    applicationId: '55555555-5555-4555-8555-000000000035',
    evaluatorId: EV_F,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-10),
    status: 'declined',
    evaluationSubmittedAt: null,
    conflictDeclared: false,
    conflictStatus: null,
    conflictDetails: null,
    conflictDeclaredAt: null,
    declineReason: 'other',
    declineNote: null,
  },
  {
    id: ASG_051_B,
    applicationId: '55555555-5555-4555-8555-000000000035',
    evaluatorId: EV_G,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-10),
    status: 'conflict_declared',
    evaluationSubmittedAt: null,
    conflictDeclared: true,
    conflictStatus: 'conflict_declared',
    conflictDetails: 'Vínculo institucional previo con la investigadora responsable.',
    conflictDeclaredAt: daysFromNow(-9),
  },
  // Assignments for AV26052 (Sensores nanotecnología — coverage: incomplete)
  {
    id: ASG_052_A,
    applicationId: '55555555-5555-4555-8555-000000000036',
    evaluatorId: EV_D,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-8),
    status: 'in_progress',
    evaluationSubmittedAt: null,
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-7),
  },
  {
    id: ASG_052_B,
    applicationId: '55555555-5555-4555-8555-000000000036',
    evaluatorId: EV_E,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-8),
    status: 'declined',
    evaluationSubmittedAt: null,
    conflictDeclared: false,
    conflictStatus: null,
    conflictDetails: null,
    conflictDeclaredAt: null,
    declineReason: 'thematic',
    declineNote: null,
  },
  // Declined invitation for AV26052 (EV_B — demo evaluator declined scenario)
  {
    id: ASG_B_052,
    applicationId: '55555555-5555-4555-8555-000000000036',
    evaluatorId: EV_B,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-9),
    status: 'declined',
    evaluationSubmittedAt: null,
    conflictDeclared: false,
    conflictStatus: null,
    conflictDetails: null,
    conflictDeclaredAt: null,
    declineReason: 'thematic',
    declineNote:
      'El proyecto se centra en sensores de nanotecnología, un área alejada de mi línea de investigación en neurociencia clínica.',
  },
  // Assignments for AV26053 (árbitro invitado — pending)
  {
    id: ASG_053_A,
    applicationId: APP_053,
    evaluatorId: EV_C,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-12),
    status: 'completed',
    evaluationSubmittedAt: daysFromNow(-4),
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-11),
  },
  {
    id: ASG_053_B,
    applicationId: APP_053,
    evaluatorId: EV_D,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-12),
    status: 'completed',
    evaluationSubmittedAt: daysFromNow(-5),
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-11),
  },
  {
    id: ASG_053_C,
    applicationId: APP_053,
    evaluatorId: EV_F,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-1),
    status: 'pending',
    evaluationSubmittedAt: null,
    conflictDeclared: false,
    conflictStatus: null,
    conflictDetails: null,
    conflictDeclaredAt: null,
  },
  // Assignments for AV26054 (árbitro sin iniciar — accepted_not_started)
  {
    id: ASG_054_A,
    applicationId: APP_054,
    evaluatorId: EV_E,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-9),
    status: 'completed',
    evaluationSubmittedAt: daysFromNow(-3),
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-8),
  },
  {
    id: ASG_054_B,
    applicationId: APP_054,
    evaluatorId: EV_G,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-9),
    status: 'completed',
    evaluationSubmittedAt: daysFromNow(-4),
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-8),
  },
  {
    id: ASG_054_C,
    applicationId: APP_054,
    evaluatorId: EV_H,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-1),
    status: 'accepted_not_started',
    evaluationSubmittedAt: null,
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: null,
  },
  // Assignments for AV26055 (Riesgo cardiovascular — requiere árbitro, sin asignar)
  {
    id: ASG_055_A,
    applicationId: APP_055,
    evaluatorId: EV_F,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-14),
    status: 'completed',
    evaluationSubmittedAt: daysFromNow(-5),
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-13),
  },
  {
    id: ASG_055_B,
    applicationId: APP_055,
    evaluatorId: EV_G,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-14),
    status: 'completed',
    evaluationSubmittedAt: daysFromNow(-6),
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-13),
  },
  // Conflict-declared invitation for AV26055 (EV_B — demo conflict scenario)
  {
    id: ASG_B_055,
    applicationId: APP_055,
    evaluatorId: EV_B,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-13),
    status: 'conflict_declared',
    evaluationSubmittedAt: null,
    conflictDeclared: true,
    conflictStatus: 'conflict_declared',
    conflictDetails:
      'Colaboración académica reciente con uno de los co-investigadores del equipo.',
    conflictDeclaredAt: daysFromNow(-12),
  },
  // Assignments for AV26056 (arbitraje finalizado)
  {
    id: ASG_056_A,
    applicationId: APP_056,
    evaluatorId: EV_A,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-20),
    status: 'completed',
    evaluationSubmittedAt: daysFromNow(-10),
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-19),
  },
  {
    id: ASG_056_B,
    applicationId: APP_056,
    evaluatorId: EV_B,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-20),
    status: 'completed',
    evaluationSubmittedAt: daysFromNow(-10),
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-19),
  },
  {
    id: ASG_056_C,
    applicationId: APP_056,
    evaluatorId: EV_D,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-8),
    status: 'completed',
    evaluationSubmittedAt: daysFromNow(-2),
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-7),
  },
  // Assignments for APP-2025-0005 (Optimización Logística — not selected)
  {
    id: ASG_008_A,
    applicationId: '55555555-5555-4555-8555-000000000008',
    evaluatorId: EV_C,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-403),
    status: 'conflict_declared',
    evaluationSubmittedAt: null,
    conflictDeclared: true,
    conflictStatus: 'conflict_declared',
    conflictDetails: 'Co-autoría reciente con el postulante.',
    conflictDeclaredAt: daysFromNow(-402),
  },
  {
    id: ASG_008_B,
    applicationId: '55555555-5555-4555-8555-000000000008',
    evaluatorId: EV_D,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-403),
    status: 'completed',
    evaluationSubmittedAt: daysFromNow(-380),
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: daysFromNow(-402),
  },
  // Assignments for AV26057 (evaluando — ambos in_progress)
  {
    id: ASG_057_A,
    applicationId: APP_057,
    evaluatorId: EV_C,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-3),
    status: 'in_progress',
    evaluationSubmittedAt: null,
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: null,
  },
  {
    id: ASG_057_B,
    applicationId: APP_057,
    evaluatorId: EV_D,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-3),
    status: 'in_progress',
    evaluationSubmittedAt: null,
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: null,
  },
  // Assignments for AV26058 (evaluando — in_progress + accepted_not_started)
  {
    id: ASG_058_A,
    applicationId: APP_058,
    evaluatorId: EV_E,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-5),
    status: 'in_progress',
    evaluationSubmittedAt: null,
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: null,
  },
  {
    id: ASG_058_B,
    applicationId: APP_058,
    evaluatorId: EV_F,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-4),
    status: 'accepted_not_started',
    evaluationSubmittedAt: null,
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: null,
  },
  // Assignments for AV26059 (evaluando — ambos accepted_not_started)
  {
    id: ASG_059_A,
    applicationId: APP_059,
    evaluatorId: EV_G,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-2),
    status: 'accepted_not_started',
    evaluationSubmittedAt: null,
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: null,
  },
  {
    id: ASG_059_B,
    applicationId: APP_059,
    evaluatorId: EV_H,
    assignedBy: ADMIN_DINV,
    assignedAt: daysFromNow(-2),
    status: 'accepted_not_started',
    evaluationSubmittedAt: null,
    conflictDeclared: false,
    conflictStatus: 'no_conflict',
    conflictDetails: null,
    conflictDeclaredAt: null,
  },
  // ── Sin disponibilidad — Pedro Alarcón (EV_I): activeLoad=10, disponible=0 ─
  // 6 in_progress + 4 accepted_not_started = activeLoad 10
  ...([
    [APP_060,'0030','in_progress'],[APP_061,'0031','in_progress'],[APP_062,'0032','in_progress'],
    [APP_063,'0033','in_progress'],[APP_064,'0034','in_progress'],[APP_065,'0035','in_progress'],
    [APP_066,'0036','accepted_not_started'],[APP_067,'0037','accepted_not_started'],
    [APP_068,'0038','accepted_not_started'],[APP_069,'0039','accepted_not_started'],
  ] as [string,string,StoredAssignment['status']][]).map(([appId, hex, status]) => ({
    id: `66666666-6666-4666-8666-0000000000${hex}`,
    applicationId: appId, evaluatorId: EV_I,
    assignedBy: ADMIN_DINV, assignedAt: daysFromNow(-5),
    status, evaluationSubmittedAt: null,
    conflictDeclared: false, conflictStatus: 'no_conflict' as const,
    conflictDetails: null, conflictDeclaredAt: null,
  })),
  // ── Sin disponibilidad — Sofía Vidal (EV_J): activeLoad=12, disponible=-2 ──
  // 8 in_progress + 2 pending + 2 accepted_not_started = activeLoad 12
  ...([
    [APP_060,'003A','in_progress'],[APP_061,'003B','in_progress'],[APP_062,'003C','in_progress'],
    [APP_063,'003D','in_progress'],[APP_064,'003E','in_progress'],[APP_065,'003F','in_progress'],
    [APP_066,'0040','in_progress'],[APP_067,'0041','in_progress'],
    [APP_068,'0042','pending'],[APP_069,'0043','pending'],
    [APP_06A,'0044','accepted_not_started'],[APP_06B,'0045','accepted_not_started'],
  ] as [string,string,StoredAssignment['status']][]).map(([appId, hex, status]) => ({
    id: `66666666-6666-4666-8666-0000000000${hex}`,
    applicationId: appId, evaluatorId: EV_J,
    assignedBy: ADMIN_DINV, assignedAt: daysFromNow(-6),
    status, evaluationSubmittedAt: null,
    conflictDeclared: false, conflictStatus: 'no_conflict' as const,
    conflictDetails: null, conflictDeclaredAt: null,
  })),
  // ── Sin disponibilidad — Matías Rojas (EV_K): activeLoad=11, disponible=-1 ─
  // 7 in_progress + 4 pending = activeLoad 11
  ...([
    [APP_060,'0046','in_progress'],[APP_061,'0047','in_progress'],[APP_062,'0048','in_progress'],
    [APP_063,'0049','in_progress'],[APP_064,'004A','in_progress'],[APP_065,'004B','in_progress'],
    [APP_066,'004C','in_progress'],
    [APP_067,'004D','pending'],[APP_068,'004E','pending'],[APP_069,'004F','pending'],
    [APP_06A,'0050','pending'],
  ] as [string,string,StoredAssignment['status']][]).map(([appId, hex, status]) => ({
    id: `66666666-6666-4666-8666-0000000000${hex}`,
    applicationId: appId, evaluatorId: EV_K,
    assignedBy: ADMIN_DINV, assignedAt: daysFromNow(-7),
    status, evaluationSubmittedAt: null,
    conflictDeclared: false, conflictStatus: 'no_conflict' as const,
    conflictDetails: null, conflictDeclaredAt: null,
  })),
];

const criteria = (scores: number[], comments: string[] = []) => {
  const out: Record<string, { score: number; comments: string | null; maxScore: number }> = {};
  CRITERIA_KEYS.forEach((key, i) => {
    out[key] = { score: scores[i], comments: comments[i] ?? null, maxScore: 5 };
  });
  return out;
};

const buildEvaluations = (): StoredEvaluation[] => [
  {
    id: '77777777-7777-4777-8777-000000000006',
    assignmentId: ASG_A,
    applicationId: APP_UNDER_REVIEW,
    evaluatorId: EV_A,
    criteriaEvaluations: criteria(
      [3, 3, 4, 3, 3, 4],
      ['Metodología aceptable, margen de mejora.', 'Plan factible pero ambicioso.', '', '', '', '']
    ),
    budgetComments: '',
    overallComments: 'El proyecto requiere ajustes metodológicos antes de ser financiado.',
    conflictsDeclared: false,
    conflictDetails: null,
    submittedAt: daysFromNow(-2),
    createdAt: daysFromNow(-7),
    updatedAt: daysFromNow(-2),
  },
  {
    id: '77777777-7777-4777-8777-000000000001',
    assignmentId: ASG_B,
    applicationId: APP_UNDER_REVIEW,
    evaluatorId: EV_B,
    criteriaEvaluations: criteria(
      [4, 3, 4, 3, 3, 4],
      ['Buen aporte al estado del arte.', 'Plan de trabajo razonable.', '', '', '', '']
    ),
    budgetComments: '',
    overallComments: '',
    conflictsDeclared: false,
    conflictDetails: null,
    submittedAt: null,
    createdAt: daysFromNow(-2),
    updatedAt: daysFromNow(-1),
  },
  {
    id: '77777777-7777-4777-8777-000000000002',
    assignmentId: ASG_D,
    applicationId: APP_UNDER_REVIEW,
    evaluatorId: EV_D,
    criteriaEvaluations: criteria(
      [5, 5, 5, 4, 4, 5],
      [
        'Excelente.',
        'Sólida.',
        'Metodología rigurosa.',
        'Equipo competente.',
        'Presupuesto justificado.',
        'Resultados esperables de alto impacto.',
      ]
    ),
    budgetComments: 'Presupuesto adecuado para los objetivos del proyecto.',
    overallComments: 'Proyecto recomendado para financiamiento.',
    conflictsDeclared: false,
    conflictDetails: null,
    submittedAt: daysFromNow(-1),
    createdAt: daysFromNow(-2),
    updatedAt: daysFromNow(-1),
  },
  // AV26053 (Epistemología — árbitro invitado): EV_C=3.00, EV_D=4.83 → desv. 1.83
  {
    id: '77777777-7777-4777-8777-000000000053',
    assignmentId: ASG_053_A,
    applicationId: APP_053,
    evaluatorId: EV_C,
    criteriaEvaluations: criteria(
      [3, 3, 3, 3, 3, 3],
      ['Propuesta con alcance limitado.', 'Relevancia moderada.', '', '', '', '']
    ),
    budgetComments: null,
    overallComments: 'Se requieren ajustes sustantivos antes de financiar.',
    conflictsDeclared: false,
    conflictDetails: null,
    submittedAt: daysFromNow(-4),
    createdAt: daysFromNow(-11),
    updatedAt: daysFromNow(-4),
  },
  {
    id: '77777777-7777-4777-8777-000000000054',
    assignmentId: ASG_053_B,
    applicationId: APP_053,
    evaluatorId: EV_D,
    criteriaEvaluations: criteria(
      [5, 5, 4, 5, 5, 5],
      ['Aporte original muy significativo.', 'Alta relevancia nacional.', '', 'Colaboración internacional sólida.', '', 'Resultados de alto impacto esperado.']
    ),
    budgetComments: null,
    overallComments: 'Proyecto de excelencia, altamente recomendado.',
    conflictsDeclared: false,
    conflictDetails: null,
    submittedAt: daysFromNow(-5),
    createdAt: daysFromNow(-11),
    updatedAt: daysFromNow(-5),
  },
  // AV26054 (Energía renovable — árbitro sin iniciar): EV_E=2.50, EV_G=4.00 → desv. 1.50
  {
    id: '77777777-7777-4777-8777-000000000055',
    assignmentId: ASG_054_A,
    applicationId: APP_054,
    evaluatorId: EV_E,
    criteriaEvaluations: criteria(
      [2, 3, 2, 3, 2, 3],
      ['Originalidad insuficiente.', '', 'Metodología débil.', '', '', '']
    ),
    budgetComments: null,
    overallComments: 'El proyecto no cumple estándares mínimos para financiamiento.',
    conflictsDeclared: false,
    conflictDetails: null,
    submittedAt: daysFromNow(-3),
    createdAt: daysFromNow(-8),
    updatedAt: daysFromNow(-3),
  },
  {
    id: '77777777-7777-4777-8777-000000000056',
    assignmentId: ASG_054_B,
    applicationId: APP_054,
    evaluatorId: EV_G,
    criteriaEvaluations: criteria(
      [4, 4, 4, 4, 4, 4],
      ['Contribución sólida.', 'Impacto territorial relevante.', '', '', '', '']
    ),
    budgetComments: null,
    overallComments: 'Proyecto bien fundamentado y con impacto claro.',
    conflictsDeclared: false,
    conflictDetails: null,
    submittedAt: daysFromNow(-4),
    createdAt: daysFromNow(-8),
    updatedAt: daysFromNow(-4),
  },
  // AV26055 (Riesgo cardiovascular — requiere árbitro): EV_F=2.33, EV_G=4.33 → desv. 2.0
  {
    id: '77777777-7777-4777-8777-000000000057',
    assignmentId: ASG_055_A,
    applicationId: APP_055,
    evaluatorId: EV_F,
    criteriaEvaluations: criteria(
      [2, 2, 3, 2, 2, 3],
      ['Originalidad insuficiente para el estado del arte.', 'Relevancia acotada.', '', '', '', '']
    ),
    budgetComments: null,
    overallComments: 'El proyecto necesita mayor fundamentación teórica y ajuste metodológico.',
    conflictsDeclared: false,
    conflictDetails: null,
    submittedAt: daysFromNow(-5),
    createdAt: daysFromNow(-13),
    updatedAt: daysFromNow(-5),
  },
  {
    id: '77777777-7777-4777-8777-000000000058',
    assignmentId: ASG_055_B,
    applicationId: APP_055,
    evaluatorId: EV_G,
    criteriaEvaluations: criteria(
      [4, 4, 5, 4, 4, 5],
      ['Aporte novedoso y bien contextualizado.', 'Impacto en salud pública.', '', '', '', 'Resultados medibles y de alto valor.']
    ),
    budgetComments: null,
    overallComments: 'Proyecto sólido, metodología rigurosa y relevancia clínica clara.',
    conflictsDeclared: false,
    conflictDetails: null,
    submittedAt: daysFromNow(-6),
    createdAt: daysFromNow(-13),
    updatedAt: daysFromNow(-6),
  },
  // AV26056 (Autoinmunes — arbitraje finalizado): EV_A≈2.83, EV_B≈4.83 → desv. 2.0; EV_D≈3.83 (árbitro)
  {
    id: '77777777-7777-4777-8777-000000000059',
    assignmentId: ASG_056_A,
    applicationId: APP_056,
    evaluatorId: EV_A,
    criteriaEvaluations: criteria(
      [3, 3, 2, 3, 3, 3],
      ['Originalidad limitada en el contexto actual.', 'Relevancia moderada.', '', '', '', '']
    ),
    budgetComments: null,
    overallComments: 'El proyecto requiere mayor profundidad metodológica.',
    conflictsDeclared: false,
    conflictDetails: null,
    submittedAt: daysFromNow(-10),
    createdAt: daysFromNow(-20),
    updatedAt: daysFromNow(-10),
  },
  {
    id: '77777777-7777-4777-8777-00000000005A',
    assignmentId: ASG_056_B,
    applicationId: APP_056,
    evaluatorId: EV_B,
    criteriaEvaluations: criteria(
      [5, 5, 5, 4, 5, 5],
      ['Aporte altamente original.', 'Relevancia nacional e internacional.', '', 'Red de colaboración sólida.', '', 'Resultados esperados de gran impacto.']
    ),
    budgetComments: null,
    overallComments: 'Proyecto sobresaliente, altamente recomendado para financiamiento.',
    conflictsDeclared: false,
    conflictDetails: null,
    submittedAt: daysFromNow(-10),
    createdAt: daysFromNow(-20),
    updatedAt: daysFromNow(-10),
  },
  {
    id: '77777777-7777-4777-8777-00000000005B',
    assignmentId: ASG_056_C,
    applicationId: APP_056,
    evaluatorId: EV_D,
    criteriaEvaluations: criteria(
      [4, 3, 4, 4, 4, 4],
      ['Aporte relevante con alcance claro.', '', 'Metodología adecuada.', '', '', '']
    ),
    budgetComments: null,
    overallComments: 'El proyecto tiene mérito pero requiere ajustes menores antes de financiar.',
    conflictsDeclared: false,
    conflictDetails: null,
    submittedAt: daysFromNow(-2),
    createdAt: daysFromNow(-8),
    updatedAt: daysFromNow(-2),
  },
  // APP-2025-0004 (Biomateriales — ADJUDICADA)
  {
    id: '77777777-7777-4777-8777-000000000007',
    assignmentId: ASG_007_A,
    applicationId: '55555555-5555-4555-8555-000000000007',
    evaluatorId: EV_A,
    criteriaEvaluations: criteria(
      [5, 5, 4, 4, 5, 5],
      ['Enfoque muy original.', 'Alta relevancia nacional.', '', '', 'Metodología sólida.', 'Resultados de alto impacto.']
    ),
    budgetComments: null,
    overallComments: 'Proyecto altamente recomendado.',
    conflictsDeclared: false,
    conflictDetails: null,
    submittedAt: daysFromNow(-385),
    createdAt: daysFromNow(-405),
    updatedAt: daysFromNow(-385),
  },
  {
    id: '77777777-7777-4777-8777-000000000008',
    assignmentId: ASG_007_B,
    applicationId: '55555555-5555-4555-8555-000000000007',
    evaluatorId: EV_B,
    criteriaEvaluations: criteria(
      [4, 5, 4, 3, 4, 5],
      ['', 'Aporte significativo al país.', '', '', '', '']
    ),
    budgetComments: 'Presupuesto bien justificado.',
    overallComments: 'Recomiendo su financiamiento.',
    conflictsDeclared: false,
    conflictDetails: null,
    submittedAt: daysFromNow(-382),
    createdAt: daysFromNow(-405),
    updatedAt: daysFromNow(-382),
  },
  // APP-2026-0022 (Computación Cuántica — EVALUADA)
  {
    id: '77777777-7777-4777-8777-000000000020',
    assignmentId: ASG_018_A,
    applicationId: '55555555-5555-4555-8555-000000000018',
    evaluatorId: EV_A,
    criteriaEvaluations: criteria(
      [5, 4, 5, 4, 4, 5],
      ['Enfoque muy innovador.', 'Alta relevancia científica.', '', 'Equipo sólido.', '', 'Resultados de alto impacto.']
    ),
    budgetComments: null,
    overallComments: 'Proyecto de frontera científica, altamente recomendado.',
    conflictsDeclared: false,
    conflictDetails: null,
    submittedAt: daysFromNow(-9),
    createdAt: daysFromNow(-15),
    updatedAt: daysFromNow(-9),
  },
  {
    id: '77777777-7777-4777-8777-000000000021',
    assignmentId: ASG_018_B,
    applicationId: '55555555-5555-4555-8555-000000000018',
    evaluatorId: EV_B,
    criteriaEvaluations: criteria(
      [4, 4, 4, 3, 4, 4],
      ['', 'Relevancia reconocida.', '', '', '', '']
    ),
    budgetComments: 'Presupuesto ajustado a la magnitud del proyecto.',
    overallComments: 'Recomiendo financiamiento.',
    conflictsDeclared: false,
    conflictDetails: null,
    submittedAt: daysFromNow(-8),
    createdAt: daysFromNow(-15),
    updatedAt: daysFromNow(-8),
  },
  // APP-2026-0023 (Vacunas ARNm — EVALUADA)
  {
    id: '77777777-7777-4777-8777-000000000022',
    assignmentId: ASG_019_A,
    applicationId: '55555555-5555-4555-8555-000000000019',
    evaluatorId: EV_C,
    criteriaEvaluations: criteria(
      [3, 4, 3, 3, 3, 4],
      ['', '', 'Metodología adecuada.', '', '', '']
    ),
    budgetComments: null,
    overallComments: 'El proyecto requiere mayor precisión metodológica.',
    conflictsDeclared: false,
    conflictDetails: null,
    submittedAt: daysFromNow(-8),
    createdAt: daysFromNow(-14),
    updatedAt: daysFromNow(-8),
  },
  {
    id: '77777777-7777-4777-8777-000000000023',
    assignmentId: ASG_019_B,
    applicationId: '55555555-5555-4555-8555-000000000019',
    evaluatorId: EV_D,
    criteriaEvaluations: criteria(
      [4, 5, 4, 4, 3, 4],
      ['', 'Urgencia sanitaria clara.', '', '', '', '']
    ),
    budgetComments: 'Considerar reducir costos en equipamiento.',
    overallComments: 'Proyecto relevante, apruebo con observaciones menores.',
    conflictsDeclared: false,
    conflictDetails: null,
    submittedAt: daysFromNow(-7),
    createdAt: daysFromNow(-14),
    updatedAt: daysFromNow(-7),
  },
  // APP-2026-0024 (Pesca Artesanal — EVALUADA)
  {
    id: '77777777-7777-4777-8777-000000000024',
    assignmentId: ASG_01A_A,
    applicationId: '55555555-5555-4555-8555-00000000001A',
    evaluatorId: EV_E,
    criteriaEvaluations: criteria(
      [4, 4, 3, 4, 3, 4],
      ['', '', '', 'Equipo multidisciplinario.', '', '']
    ),
    budgetComments: null,
    overallComments: 'Investigación necesaria para políticas públicas pesqueras.',
    conflictsDeclared: false,
    conflictDetails: null,
    submittedAt: daysFromNow(-7),
    createdAt: daysFromNow(-13),
    updatedAt: daysFromNow(-7),
  },
  {
    id: '77777777-7777-4777-8777-000000000025',
    assignmentId: ASG_01A_B,
    applicationId: '55555555-5555-4555-8555-00000000001A',
    evaluatorId: EV_A,
    criteriaEvaluations: criteria(
      [3, 3, 4, 3, 3, 3],
      ['', '', 'Diseño metodológico adecuado.', '', '', '']
    ),
    budgetComments: null,
    overallComments: 'Se recomienda ampliar el marco teórico.',
    conflictsDeclared: false,
    conflictDetails: null,
    submittedAt: daysFromNow(-6),
    createdAt: daysFromNow(-13),
    updatedAt: daysFromNow(-6),
  },
  // APP-2025-0005 (Optimización Logística — NO SELECCIONADA)
  {
    id: '77777777-7777-4777-8777-000000000010',
    assignmentId: ASG_008_B,
    applicationId: '55555555-5555-4555-8555-000000000008',
    evaluatorId: EV_D,
    criteriaEvaluations: criteria(
      [3, 3, 2, 2, 2, 3],
      ['', '', '', '', 'Plan de trabajo poco desarrollado.', '']
    ),
    budgetComments: null,
    overallComments: 'Requiere revisión profunda antes de considerar financiamiento.',
    conflictsDeclared: false,
    conflictDetails: null,
    submittedAt: daysFromNow(-380),
    createdAt: daysFromNow(-403),
    updatedAt: daysFromNow(-380),
  },
];

// ---------------------------------------------------------------------------
// Alerts
// ---------------------------------------------------------------------------

const buildAlerts = (): ApplicationAlert[] => [
  {
    id: '88888888-8888-4888-8888-000000000001',
    applicationId: APP_UNDER_REVIEW,
    alertType: 'evaluation_score_variance' as any,
    title: 'Desviación de puntajes detectada',
    description: 'La diferencia de puntajes entre evaluadores supera el umbral.',
    priority: 'high' as any,
    status: 'open' as any,
    createdAt: new Date(daysFromNow(-1)),
    resolvedAt: null,
    resolvedBy: null,
    resolutionNote: null,
  },
  {
    id: '88888888-8888-4888-8888-000000000002',
    applicationId: '55555555-5555-4555-8555-000000000004',
    alertType: 'evaluation_delayed' as any,
    title: 'Evaluación no enviada dentro del plazo',
    description: 'La evaluación aceptada lleva más de 7 días sin ser enviada.',
    priority: 'medium' as any,
    status: 'open' as any,
    createdAt: new Date(daysFromNow(-2)),
    resolvedAt: null,
    resolvedBy: null,
    resolutionNote: null,
  },
  {
    id: '88888888-8888-4888-8888-000000000003',
    applicationId: '55555555-5555-4555-8555-000000000016',
    alertType: 'manual' as any,
    title: 'Revisión adicional requerida',
    description: 'Se solicitó revisión adicional de la postulación antes de continuar.',
    priority: 'medium' as any,
    status: 'open' as any,
    createdAt: new Date(daysFromNow(-1)),
    resolvedAt: null,
    resolvedBy: null,
    resolutionNote: null,
  },
  {
    id: '88888888-8888-4888-8888-000000000006',
    applicationId: '55555555-5555-4555-8555-000000000017',
    alertType: 'invitation_rejected' as any,
    title: 'Evaluador declaró conflicto de interés',
    description: 'Un evaluador asignado declaró conflicto de interés con el investigador principal.',
    priority: 'high' as any,
    status: 'open' as any,
    createdAt: new Date(daysFromNow(-1)),
    resolvedAt: null,
    resolvedBy: null,
    resolutionNote: null,
  },
  {
    id: '88888888-8888-4888-8888-000000000004',
    applicationId: '55555555-5555-4555-8555-000000000004',
    alertType: 'manual' as any,
    title: 'Documentación faltante',
    description: 'Faltaba carta de compromiso institucional.',
    priority: 'low' as any,
    status: 'resolved' as any,
    createdAt: new Date(daysFromNow(-3)),
    resolvedAt: new Date(daysFromNow(-1)),
    resolvedBy: ADMIN_DINV,
    resolutionNote: 'Documentación cargada correctamente por el postulante.',
  },
  {
    id: '88888888-8888-4888-8888-000000000005',
    applicationId: APP_UNDER_REVIEW,
    alertType: 'invitation_rejected' as any,
    title: 'Evaluador declaró conflicto de interés',
    description: 'Un evaluador declaró conflicto de interés con el equipo de investigación.',
    priority: 'medium' as any,
    status: 'dismissed' as any,
    createdAt: new Date(daysFromNow(-2)),
    resolvedAt: null,
    resolvedBy: null,
    resolutionNote: null,
  },
  // Extended type alerts — polymorphic anchoring
  {
    id: '88888888-8888-4888-8888-000000000007',
    applicationId: APP_UNDER_REVIEW,
    evaluatorId: EV_E,
    assignmentId: ASG_E,
    alertType: 'invitation_no_response' as any,
    title: 'Invitación sin respuesta',
    description: 'Lucía Herrera no ha respondido a la invitación de evaluación en más de 3 días.',
    priority: 'medium' as any,
    status: 'open' as any,
    createdAt: new Date(daysFromNow(-1)),
    resolvedAt: null,
    resolvedBy: null,
    resolutionNote: null,
  },
  {
    id: '88888888-8888-4888-8888-000000000008',
    applicationId: null,
    evaluatorId: EV_C,
    alertType: 'evaluator_no_login' as any,
    title: 'Evaluador sin actividad en la plataforma',
    description: 'Elena Vargas no ha tenido actividad en la plataforma en 7 días y tiene evaluaciones pendientes.',
    priority: 'high' as any,
    status: 'open' as any,
    createdAt: new Date(daysFromNow(-2)),
    resolvedAt: null,
    resolvedBy: null,
    resolutionNote: null,
  },
  {
    id: '88888888-8888-4888-8888-000000000009',
    applicationId: null,
    evaluatorId: EV_A,
    alertType: 'evaluator_overloaded' as any,
    title: 'Evaluador con sobrecarga de asignaciones',
    description: 'Patricia Soto tiene 4 asignaciones activas, superando el límite recomendado de 3.',
    priority: 'medium' as any,
    status: 'open' as any,
    createdAt: new Date(daysFromNow(-1)),
    resolvedAt: null,
    resolvedBy: null,
    resolutionNote: null,
  },
  {
    id: '88888888-8888-4888-8888-00000000000A',
    applicationId: APP_UNDER_REVIEW,
    evaluatorId: EV_A,
    assignmentId: ASG_A,
    alertType: 'evaluation_delayed' as any,
    title: 'Evaluación no completada',
    description: 'Patricia Soto lleva 10 días con la asignación activa y aún no ha enviado la evaluación.',
    priority: 'medium' as any,
    status: 'open' as any,
    createdAt: new Date(daysFromNow(-1)),
    resolvedAt: null,
    resolvedBy: null,
    resolutionNote: null,
  },
  {
    id: '88888888-8888-4888-8888-00000000000C',
    applicationId: APP_UNDER_REVIEW,
    evaluatorId: EV_B,
    assignmentId: ASG_B,
    alertType: 'evaluation_delayed' as any,
    title: 'Evaluación muy atrasada',
    description: 'Roberto Méndez lleva 15 días con la asignación activa y aún no ha enviado la evaluación.',
    priority: 'high' as any,
    status: 'open' as any,
    createdAt: new Date(daysFromNow(-2)),
    resolvedAt: null,
    resolvedBy: null,
    resolutionNote: null,
  },
  {
    id: '88888888-8888-4888-8888-00000000000B',
    applicationId: APP_UNDER_REVIEW,
    evaluatorId: EV_C,
    assignmentId: ASG_C,
    alertType: 'invitation_rejected' as any,
    title: 'Evaluador declaró conflicto de interés',
    description: 'Elena Vargas declaró conflicto de interés: co-autoría reciente con el postulante.',
    priority: 'high' as any,
    status: 'open' as any,
    createdAt: new Date(daysFromNow(-2)),
    resolvedAt: null,
    resolvedBy: null,
    resolutionNote: null,
  },
];

// ---------------------------------------------------------------------------
// Files (point at the static templates bundled in /public/templates)
// ---------------------------------------------------------------------------

const mkFile = (
  id: string,
  applicationId: string,
  sectionType: SectionType,
  originalName: string,
  downloadUrl: string,
  mimeType = DOCX,
  size = 256000
): StoredFile => ({
  id,
  applicationId,
  sectionType,
  filename: originalName,
  originalName,
  mimeType,
  size,
  uploadedAt: daysFromNow(-2),
  downloadUrl,
});

const buildFiles = (): StoredFile[] => {
  const filesForApp = (appId: string, prefix: string): StoredFile[] => [
    mkFile(
      `${prefix}-project`,
      appId,
      'PROJECT_DESCRIPTION',
      'Formulacion_Proyecto.docx',
      '/templates/Avanza_UC_Formulacion Proyecto.docx'
    ),
    mkFile(
      `${prefix}-budget`,
      appId,
      'PROJECT_RESOURCES',
      'Presupuesto_AvanzaUC2025.xlsx',
      '/templates/Presupuesto_AvanzaUC2025.xlsx',
      XLSX,
      158000
    ),
    mkFile(
      `${prefix}-pub1`,
      appId,
      'PROJECT_ATTACHMENTS',
      'Carta_Apoyo.docx',
      '/templates/CARTA APOYO SPA.docx'
    ),
    mkFile(
      `${prefix}-coi`,
      appId,
      'PROJECT_ATTACHMENTS',
      'Carta_COI_Compromiso.docx',
      '/templates/CARTA COI COMPROMISO SPA.docx'
    ),
  ];

  return [
    ...filesForApp('55555555-5555-4555-8555-000000000004', 'file-004'),
    ...filesForApp('55555555-5555-4555-8555-000000000005', 'file-005'),
    ...filesForApp(APP_UNDER_REVIEW, 'file-006'),
    ...filesForApp('55555555-5555-4555-8555-000000000007', 'file-007'),
    ...filesForApp('55555555-5555-4555-8555-000000000008', 'file-008'),
    ...filesForApp('55555555-5555-4555-8555-000000000009', 'file-009'),
    // AV26006 (demo evaluador): declaraciones DOI de IR e IP (sección Publicaciones y CV).
    mkFile('file-006-doi-ir', APP_UNDER_REVIEW, 'PROJECT_ATTACHMENTS', 'DOI_IR.docx', '/templates/FORMATO DOI IR.docx'),
    mkFile('file-006-doi-ip', APP_UNDER_REVIEW, 'PROJECT_ATTACHMENTS', 'DOI_IP.docx', '/templates/FORMATO DOI IP.docx'),
    // Archivos adjuntos de cada producción académica (evidencia de acceso
    // requerida, 2026-07-21). En el demo apuntan a un PDF real para que el
    // evaluador pueda previsualizarlos.
    ...[1, 2, 3, 4, 5].flatMap((n) => [
      mkFile(`file-006-pub-ir-${n}`, APP_UNDER_REVIEW, 'PROJECT_ATTACHMENTS', `Publicacion_IR_${n}.pdf`, '/templates/Bases_Avanza_UC.pdf', PDF, 320000),
      mkFile(`file-006-pub-ip-${n}`, APP_UNDER_REVIEW, 'PROJECT_ATTACHMENTS', `Publicacion_IP_${n}.pdf`, '/templates/Bases_Avanza_UC.pdf', PDF, 320000),
    ]),
  ];
};

// ---------------------------------------------------------------------------
// Build the full seed dataset (fresh deep copy every call).
// ---------------------------------------------------------------------------

const buildContestExclusions = () => [
  // EV_H excluida del concurso abierto (sin confirmar, conflicto de agenda)
  { templateId: T_OPEN, evaluatorId: EV_H },
];

const buildEvaluatorProfiles = (): EvaluatorAdminProfile[] => [
  {
    evaluatorId: EV_A,
    score: 4.2,
    notes: [
      {
        id: 'note-ev-a-1',
        text: 'Buena capacidad analítica. Tiende a ser conservador en proyectos de alto riesgo. Recomendado con seguimiento.',
        createdAt: daysFromNow(-90),
        createdByName: 'Admin DINV',
      },
    ],
  },
  {
    evaluatorId: EV_B,
    score: 4.7,
    notes: [
      {
        id: 'note-ev-b-1',
        text: 'Excelente disposición y puntualidad en entregas. Evaluaciones muy bien fundamentadas. Priorizar en futuros concursos.',
        createdAt: daysFromNow(-60),
        createdByName: 'Admin DINV',
      },
    ],
  },
  {
    evaluatorId: EV_C,
    score: 3.8,
    notes: [
      {
        id: 'note-ev-c-1',
        text: 'Declaró conflicto de interés en concurso anterior por colaboración con postulante. Verificar antes de asignar.',
        createdAt: daysFromNow(-45),
        createdByName: 'Admin DINV',
      },
    ],
  },
  {
    evaluatorId: EV_D,
    score: 4.5,
    notes: [
      {
        id: 'note-ev-d-1',
        text: 'Especialista en ciencias exactas e ingeniería. Evitar asignar proyectos de ciencias sociales o humanidades.',
        createdAt: daysFromNow(-20),
        createdByName: 'Admin DINV',
      },
    ],
  },
  {
    evaluatorId: EV_E,
    score: null,
    notes: [],
  },
  {
    evaluatorId: EV_F,
    score: 4.0,
    notes: [
      {
        id: 'note-ev-f-1',
        text: 'Alta carga en concursos anteriores. Considerar no asignar más de 5 postulaciones por concurso.',
        createdAt: daysFromNow(-30),
        createdByName: 'Admin DINV',
      },
    ],
  },
  {
    evaluatorId: EV_G,
    score: 4.1,
    notes: [
      {
        id: 'note-ev-g-1',
        text: 'Indicó disponibilidad limitada en el primer semestre por carga académica. Coordinar invitaciones a partir de julio.',
        createdAt: daysFromNow(-15),
        createdByName: 'Admin DINV',
      },
    ],
  },
  {
    evaluatorId: EV_H,
    score: null,
    notes: [],
  },
];

export const buildSeedData = (): SeedData => ({
  // The generated 2026 cohort is appended to the hand-written records, never
  // replaces them: the demo scenarios in buildApplications/buildAssignments stay
  // exactly as they were.
  users: [...buildUsers(), ...buildCohortUsers()],
  formTemplates: buildFormTemplates(),
  applications: [...buildApplications(), ...buildCohortApplicationRecords()],
  assignments: [...buildAssignments(), ...buildCohortAssignmentRecords()],
  evaluations: buildEvaluations(),
  alerts: buildAlerts(),
  files: buildFiles(),
  evaluatorProfiles: buildEvaluatorProfiles(),
  reminders: [],
  contestExclusions: buildContestExclusions(),
});
